import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type FormEvent,
  type ReactNode,
} from "react";
import { Link, useMatch, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  KeyRound,
  Users as UsersIcon,
  ListChecks,
  Plus,
  RefreshCcw,
  Filter,
  Search,
  Copy,
  Check,
  Trash2,
  ShieldCheck,
  Building2,
  Mail,
  Code2,
  Info,
  UserCog,
  UserMinus,
  UserPlus,
  Database,
  Download,
  Play,
  Table as TableIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Ban,
  CircleCheck,
  PencilLine,
  UserRoundSearch,
  X,
} from "lucide-react";
import {
  SfFieldGrid,
  SfListShell,
  SfRecordHero,
  SfRowMenu,
  SfRowMenuBtn,
  SfSidebarCard,
  SfSplitDetail,
  SfTableWrap,
} from "@/components/RecordChrome";
import { Sidebar, type NavKey } from "@/components/Sidebar";
import { Topbar } from "@/components/Topbar";
import { ToastBanner } from "@/components/Toast";
import { SecuritySection } from "@/pages/sections/SecuritySection";
import { SecretsSection } from "@/pages/sections/SecretsSection";
import { WebhooksSection } from "@/pages/sections/WebhooksSection";
import { ApprovalsSection } from "@/pages/sections/ApprovalsSection";
import { RotationSection } from "@/pages/sections/RotationSection";
import type { SecretRow } from "@/lib/secretTypes";
import { cn } from "@/lib/cn";
import { slugifyOrgNamePreview } from "@/lib/orgSlug";
import {
  adminActivateDirectoryUser,
  adminActivateOrganization,
  adminAddMember,
  adminListClientAdminUsers,
  adminCreateOrganization,
  adminDeactivateDirectoryUser,
  adminDeactivateOrganization,
  adminDeleteDirectoryUser,
  adminDeleteOrganization,
  adminDirectoryUserDetail,
  adminDirectoryUsers,
  adminListOrgMembers,
  adminListOrgs,
  adminListTables,
  adminPatchDirectoryUser,
  adminRemoveMember,
  adminResetDirectoryUserPassword,
  adminRunQuery,
  adminSetMemberRole,
  adminTableColumns,
  adminTableRows,
  adminCreateUser,
  createApiKey,
  downloadAuditExport,
  friendlyAuthError,
  grantVaultAcl,
  fetchMyProfile,
  fetchOrgSmtpSettings,
  fetchPlatformSmtpSettings,
  patchMyProfile,
  patchOrgSmtpSettings,
  patchPlatformSmtpSettings,
  provisionOrgStaff,
  loadApiKeys,
  loadAuditLogs,
  loadMembers,
  loadMemberships,
  loadSecrets,
  loadVaults,
  logout,
  machineSecretsBaseUrl,
  revokeApiKey,
  type AdminColumn,
  type AdminDirectoryMembership,
  type AdminDirectoryUser,
  type AdminClientAdminRow,
  type AdminMemberRow,
  type AdminOrgRow,
  type AdminQueryResult,
  type AdminTable,
  type AdminWhereOp,
  type AuditLogRow,
  type OrgRole as ApiOrgRole,
  type Session,
  type SmtpSettingsPatch,
} from "@/lib/api";
import { randomApiKey, sha256Hex } from "@/lib/hash";

type OrgRole = "dataly_admin" | "client_admin" | "client_staff" | "public_user";

const ORG_ROLE_LABEL: Record<OrgRole, string> = {
  dataly_admin: "Dataly admin",
  client_admin: "Client admin",
  client_staff: "Client staff",
  public_user: "Public",
};

function orgRoleLabel(role: OrgRole): string {
  return ORG_ROLE_LABEL[role] ?? role;
}

type Org = { id: string; record_id?: string; name: string; slug: string; active?: boolean };
type Membership = { organization_id: string; role: OrgRole; organizations: Org | null };
type Vault = {
  id: string;
  record_id?: string;
  name: string;
  description: string | null;
  organization_id: string;
  requires_approval?: boolean;
};
type ApiKeyRow = {
  id: string;
  record_id?: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
  last_used_at: string | null;
};
type MemberRow = {
  membership_id?: string;
  membership_record_id?: string;
  user_id: string;
  user_record_id?: string;
  role: OrgRole;
  profiles: { display_name: string | null } | null;
};

function one<T>(x: T | T[] | null | undefined): T | null {
  if (x == null) return null;
  return Array.isArray(x) ? (x[0] ?? null) : x;
}

function normalizeMembership(row: {
  organization_id: string;
  role: string;
  organizations: Org | Org[] | null | undefined;
}): Membership {
  return {
    organization_id: row.organization_id,
    role: row.role as OrgRole,
    organizations: one(row.organizations),
  };
}

function normalizeMemberRow(row: {
  membership_id?: string;
  membership_record_id?: string;
  user_id: string;
  user_record_id?: string;
  role: string;
  profiles:
    | { display_name: string | null }
    | { display_name: string | null }[]
    | null
    | undefined;
}): MemberRow {
  return {
    membership_id: row.membership_id,
    membership_record_id: row.membership_record_id,
    user_id: row.user_id,
    user_record_id: row.user_record_id,
    role: row.role as OrgRole,
    profiles: one(row.profiles),
  };
}

const SECTION_TITLES: Record<NavKey, { title: string; subtitle: string }> = {
  overview: {
    title: "Overview",
    subtitle: "Snapshot of secrets, members, and recent activity.",
  },
  secrets: {
    title: "Secrets",
    subtitle: "Encrypted values your apps and integrations consume at runtime.",
  },
  organization: {
    title: "Organization",
    subtitle:
      "Every tenant and Client admin assignment (platform), or your workspace team (tenant roles).",
  },
  "api-keys": {
    title: "API keys",
    subtitle: "Bearer tokens for CI / automation pulling secrets over HTTPS.",
  },
  audit: {
    title: "Audit trail",
    subtitle: "Every action — by people and integrations — recorded for compliance.",
  },
  "admin-profile": {
    title: "Admin profile",
    subtitle:
      "Display name, outbound From address, and optional SMTP relay for workspace invitations.",
  },
  settings: {
    title: "Settings",
    subtitle: "Workspace details, integration endpoints, and account.",
  },
  database: {
    title: "Database explorer",
    subtitle: "Read-only browse over every Postgres table. Workbench-style filters + ad-hoc SELECT.",
  },
  directory: {
    title: "All users",
    subtitle:
      "Platform directory — edit profiles, reset passwords, deactivate accounts, or remove users.",
  },
  security: {
    title: "Security",
    subtitle: "Two-factor authentication and active sessions for your account.",
  },
  webhooks: {
    title: "Webhooks",
    subtitle: "Receive HMAC-signed POSTs when secrets change or approvals are filed.",
  },
  approvals: {
    title: "Approvals",
    subtitle: "Pending edits and deletions on approval-gated vaults — review and resolve.",
  },
  rotation: {
    title: "Rotation",
    subtitle: "Auto-rotate secret values on a schedule. Each run writes a new version + audit entry.",
  },
};

export function Dashboard({ session }: { session: Session }) {
  const navigate = useNavigate();
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [vaults, setVaults] = useState<Vault[]>([]);
  const [secrets, setSecrets] = useState<SecretRow[]>([]);
  const [apiKeys, setApiKeys] = useState<ApiKeyRow[]>([]);
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [orgId, setOrgId] = useState<string | null>(null);
  /** Latest workspace selection — used to ignore stale loadOrgData responses after switching orgs. */
  const orgWorkspaceRef = useRef<string | null>(orgId);
  const [loading, setLoading] = useState(true);
  /** Failures loading GET /memberships (distinct from empty membership list). */
  const [workspaceLoadError, setWorkspaceLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [section, setSection] = useState<NavKey>("overview");
  const [secretName, setSecretName] = useState("");
  const [secretPlain, setSecretPlain] = useState("");
  const [vaultForSecret, setVaultForSecret] = useState<string>("");
  const [apiKeyName, setApiKeyName] = useState("CI / automation");
  const [revealedKey, setRevealedKey] = useState<string | null>(null);

  const [aclUser, setAclUser] = useState("");
  const [aclVault, setAclVault] = useState("");
  const [aclPerm, setAclPerm] = useState<"read" | "read_write" | "admin">("read");

  const [staffInviteEmail, setStaffInviteEmail] = useState("");
  const [staffInviteSubmitting, setStaffInviteSubmitting] = useState(false);
  const [staffInviteVaultIds, setStaffInviteVaultIds] = useState<string[]>([]);
  const [staffInviteVaultPerm, setStaffInviteVaultPerm] = useState<
    "read" | "read_write" | "admin"
  >("read_write");
  const [staffInviteOk, setStaffInviteOk] = useState<string | null>(null);

  const [platformCreateOrgOpen, setPlatformCreateOrgOpen] = useState(false);
  const [platformCreateUserOpen, setPlatformCreateUserOpen] = useState(false);

  const [toast, setToast] = useState<{ message: string; kind?: "ok" | "err" } | null>(null);
  const showToast = useCallback((message: string, kind?: "ok" | "err") => {
    setToast({ message, kind });
  }, []);

  /** Server-side secret list filters (GET secrets query params). */
  const [secretFilters, setSecretFilters] = useState({
    q: "",
    vaultId: "",
    tagTerms: "",
  });
  const [draftServerQ, setDraftServerQ] = useState("");
  const [draftTagTerms, setDraftTagTerms] = useState("");
  const [localNameFilter, setLocalNameFilter] = useState("");

  const [auditLogs, setAuditLogs] = useState<AuditLogRow[]>([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditDraft, setAuditDraft] = useState("");
  const [auditQuery, setAuditQuery] = useState("");

  const currentRole = useMemo(() => {
    const m = memberships.find((x) => x.organization_id === orgId);
    return m?.role ?? null;
  }, [memberships, orgId]);

  /** Access vault / secrets UI (not `public_user`, not org-scoped platform admin). */
  const tenantSecretsUi =
    currentRole === "client_admin" || currentRole === "client_staff";
  /** Audit nav + API — tenant admin or platform admin for this org. */
  const canViewAuditLogs =
    currentRole === "client_admin" || currentRole === "dataly_admin";
  /** Org tab + workspace picker — not shown for self-sign-up `public_user`. */
  const showOrganizationChrome = currentRole !== "public_user";
  /** Create vaults, API keys, grant ACL — client tenant admins only. */
  const canManageTenantVaults = currentRole === "client_admin";
  /** Org picker context is `dataly_admin` — hide member workspace chrome (platform tools live elsewhere). */
  const hideTenantOrgChrome = currentRole === "dataly_admin";

  const isPlatformAdmin = useMemo(
    () => memberships.some((m) => m.role === "dataly_admin"),
    [memberships],
  );
  const platformOrgDetailMatch = useMatch({
    path: "/app/organizations/:orgId",
    end: true,
  });
  /** Dataly admin on `/app/organizations/:id` — tenant APIs must target URL org, not sidebar picker only. */
  const tenantDataOrgId = useMemo(() => {
    const detail = platformOrgDetailMatch?.params.orgId;
    if (currentRole === "dataly_admin" && detail && isPlatformAdmin) return detail;
    return orgId;
  }, [currentRole, platformOrgDetailMatch?.params.orgId, isPlatformAdmin, orgId]);
  const directoryDetailMatch = useMatch({ path: "/app/directory/:userId", end: true });
  const tenantMemberDetailMatch = useMatch({ path: "/app/members/:userId", end: true });
  /** Create empty tenants via POST /admin/orgs — Dataly admin or any client admin. */
  const canProvisionOrganizations =
    isPlatformAdmin || memberships.some((m) => m.role === "client_admin");
  const edgeBase = useMemo(() => machineSecretsBaseUrl(), []);

  const loadMembershipsCb = useCallback(async () => {
    const rows = await loadMemberships();
    const mapped = rows.map((row) =>
      normalizeMembership({
        organization_id: row.organization_id,
        role: row.role,
        organizations: row.organizations,
      }),
    );
    setMemberships(mapped);
    setOrgId((prev) => {
      if (prev && mapped.some((r) => r.organization_id === prev)) return prev;
      return mapped[0]?.organization_id ?? null;
    });
  }, []);

  useEffect(() => {
    orgWorkspaceRef.current = tenantDataOrgId;
  }, [tenantDataOrgId]);

  /** Drop vault/secret/member state immediately when switching workspace so we never flash another org's data. */
  useEffect(() => {
    setVaults([]);
    setSecrets([]);
    setApiKeys([]);
    setMembers([]);
    setVaultForSecret("");
    setSecretFilters({ q: "", vaultId: "", tagTerms: "" });
    setDraftServerQ("");
    setDraftTagTerms("");
    setLocalNameFilter("");
  }, [tenantDataOrgId]);

  const loadOrgData = useCallback(async () => {
    if (!tenantDataOrgId) {
      setVaults([]);
      setSecrets([]);
      setApiKeys([]);
      setMembers([]);
      return;
    }
    const oid = tenantDataOrgId;
    const role = memberships.find((x) => x.organization_id === oid)?.role;
    const loadKeys = role === "client_admin";

    const tagList = secretFilters.tagTerms
      .split(/[,\s]+/)
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);

    const [v, m, s] = await Promise.all([
      loadVaults(oid),
      loadMembers(oid),
      loadSecrets(oid, {
        q: secretFilters.q.trim() || undefined,
        vault_id: secretFilters.vaultId || undefined,
        tags: tagList.length ? tagList : undefined,
      }),
    ]);
    const k = loadKeys ? await loadApiKeys(oid) : [];

    if (orgWorkspaceRef.current !== oid) return;

    setVaults(v as Vault[]);
    setMembers(
      (
        m as {
          user_id: string;
          role: string;
          profiles:
            | { display_name: string | null }
            | { display_name: string | null }[]
            | null
            | undefined;
        }[]
      ).map((row) => normalizeMemberRow(row)),
    );
    setApiKeys((k as ApiKeyRow[]) ?? []);
    setSecrets(s as SecretRow[]);
  }, [tenantDataOrgId, memberships, secretFilters]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setWorkspaceLoadError(null);
      try {
        await loadMembershipsCb();
      } catch (e) {
        if (!cancelled)
          setWorkspaceLoadError(e instanceof Error ? e.message : "Load failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadMembershipsCb]);

  async function retryWorkspaceLoad() {
    setWorkspaceLoadError(null);
    setLoading(true);
    try {
      await loadMembershipsCb();
    } catch (e) {
      setWorkspaceLoadError(e instanceof Error ? e.message : "Load failed");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!tenantDataOrgId) return;
    let cancelled = false;
    (async () => {
      try {
        await loadOrgData();
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Load failed");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantDataOrgId, loadOrgData]);

  useEffect(() => {
    if (vaults.length && !vaultForSecret) setVaultForSecret(vaults[0].id);
  }, [vaults, vaultForSecret]);

  const fetchAudit = useCallback(async () => {
    if (!tenantDataOrgId || !canViewAuditLogs) {
      setAuditLogs([]);
      setAuditTotal(0);
      return;
    }
    try {
      const d = await loadAuditLogs(tenantDataOrgId, {
        limit: 40,
        offset: 0,
        action: auditQuery.trim() || undefined,
      });
      setAuditLogs(d.audit_logs);
      setAuditTotal(d.total);
    } catch {
      setAuditLogs([]);
      setAuditTotal(0);
    }
  }, [tenantDataOrgId, auditQuery, canViewAuditLogs]);

  useEffect(() => {
    void fetchAudit();
  }, [fetchAudit]);

  useEffect(() => {
    setAuditDraft("");
    setAuditQuery("");
  }, [tenantDataOrgId]);

  useEffect(() => {
    setStaffInviteEmail("");
    setStaffInviteVaultIds([]);
    setStaffInviteVaultPerm("read_write");
    setStaffInviteOk(null);
  }, [tenantDataOrgId]);

  useEffect(() => {
    if (!currentRole) return;
    if (!canViewAuditLogs && (section === "audit" || section === "admin-profile")) {
      setSection("overview");
      return;
    }
    if (hideTenantOrgChrome) {
      if (section === "secrets" || section === "api-keys") {
        setSection("overview");
      }
    } else if (currentRole === "public_user") {
      if (
        section === "secrets" ||
        section === "api-keys" ||
        section === "audit" ||
        section === "organization" ||
        section === "admin-profile"
      ) {
        setSection("overview");
      }
    }
  }, [currentRole, hideTenantOrgChrome, section, canViewAuditLogs]);

  useEffect(() => {
    if (loading) return;
    const oid = platformOrgDetailMatch?.params.orgId;
    if (oid && !isPlatformAdmin) navigate("/app", { replace: true });
  }, [loading, platformOrgDetailMatch?.params.orgId, isPlatformAdmin, navigate]);

  useEffect(() => {
    if (loading) return;
    if (platformOrgDetailMatch?.params.orgId && isPlatformAdmin) {
      setSection("organization");
    }
  }, [loading, platformOrgDetailMatch?.params.orgId, isPlatformAdmin]);

  useEffect(() => {
    if (loading) return;
    const uid = directoryDetailMatch?.params.userId;
    if (uid && !isPlatformAdmin) navigate("/app", { replace: true });
  }, [loading, directoryDetailMatch?.params.userId, isPlatformAdmin, navigate]);

  useEffect(() => {
    if (loading) return;
    if (directoryDetailMatch?.params.userId && isPlatformAdmin) setSection("directory");
  }, [loading, directoryDetailMatch?.params.userId, isPlatformAdmin]);

  useEffect(() => {
    if (loading) return;
    const uid = tenantMemberDetailMatch?.params.userId;
    if (!uid) return;
    if (
      currentRole === "dataly_admin" ||
      currentRole === "public_user" ||
      currentRole === null
    ) {
      navigate("/app", { replace: true });
      return;
    }
    setSection("organization");
  }, [loading, tenantMemberDetailMatch?.params.userId, currentRole, navigate]);

  const prevSectionRef = useRef(section);
  useEffect(() => {
    const orgPath = platformOrgDetailMatch?.params.orgId;
    const dirPath = directoryDetailMatch?.params.userId;
    const memPath = tenantMemberDetailMatch?.params.userId;
    const prev = prevSectionRef.current;

    if (prev === "organization" && section !== "organization") {
      if (orgPath && isPlatformAdmin) navigate("/app", { replace: true });
      if (
        memPath &&
        (currentRole === "client_admin" || currentRole === "client_staff")
      ) {
        navigate("/app", { replace: true });
      }
    }
    if (prev === "directory" && section !== "directory" && dirPath && isPlatformAdmin) {
      navigate("/app", { replace: true });
    }
    prevSectionRef.current = section;
  }, [
    section,
    platformOrgDetailMatch?.params.orgId,
    directoryDetailMatch?.params.userId,
    tenantMemberDetailMatch?.params.userId,
    isPlatformAdmin,
    currentRole,
    navigate,
  ]);

  async function signOut() {
    await logout();
    navigate("/", { replace: true });
  }

  async function handleCreateApiKey(e: FormEvent) {
    e.preventDefault();
    if (!tenantDataOrgId) return;
    const raw = randomApiKey();
    const key_hash = await sha256Hex(raw);
    const key_prefix = raw.slice(0, 12);
    setError(null);
    try {
      await createApiKey(
        tenantDataOrgId,
        apiKeyName.trim() || "API key",
        key_prefix,
        key_hash,
        ["secrets:read"],
        null,
      );
      setRevealedKey(raw);
      await loadOrgData();
      await fetchAudit();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed");
    }
  }

  async function revokeKey(id: string) {
    if (!tenantDataOrgId) return;
    setError(null);
    try {
      await revokeApiKey(tenantDataOrgId, id);
      await loadOrgData();
      await fetchAudit();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed");
    }
  }

  async function grantAcl(e: FormEvent) {
    e.preventDefault();
    if (!tenantDataOrgId || !aclUser || !aclVault) return;
    setError(null);
    try {
      await grantVaultAcl(tenantDataOrgId, aclUser, aclVault, aclPerm);
      await loadOrgData();
      await fetchAudit();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed");
    }
  }

  async function inviteStaff(e: FormEvent) {
    e.preventDefault();
    if (!tenantDataOrgId || !staffInviteEmail.trim()) return;
    setStaffInviteSubmitting(true);
    setError(null);
    setStaffInviteOk(null);
    try {
      const vault_access = staffInviteVaultIds.map((vault_id) => ({
        vault_id,
        permission: staffInviteVaultPerm,
      }));
      const result = await provisionOrgStaff(tenantDataOrgId, {
        email: staffInviteEmail.trim().toLowerCase(),
        vault_access,
      });
      setStaffInviteEmail("");
      setStaffInviteVaultIds([]);
      await loadOrgData();
      await fetchAudit();
      const parts: string[] = [];
      if (result.created_user) parts.push("New account created.");
      else parts.push("Existing account updated as staff.");
      if (result.email_sent) parts.push("Email sent.");
      else if (result.temporary_password)
        parts.push(`Temporary password (copy now): ${result.temporary_password}`);
      else parts.push("No email sent — check API SMTP settings.");
      setStaffInviteOk(parts.join(" "));
    } catch (err) {
      setError(err instanceof Error ? friendlyAuthError(err) : "Failed to invite");
    } finally {
      setStaffInviteSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-[var(--color-bg)]">
        <div className="flex items-center gap-3 text-sm text-[var(--color-text-muted)]">
          <div
            className="h-5 w-5 rounded-full border-2 border-[var(--color-border)] spin"
            style={{ borderTopColor: "var(--color-accent)" }}
          />
          Loading workspace…
        </div>
      </div>
    );
  }

  // Membership list failed — empty [] looks identical to "no orgs"; don't send users to bootstrap.
  if (memberships.length === 0 && workspaceLoadError) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-[var(--color-bg)] px-4 py-10">
        <div
          className="w-full max-w-lg rounded-2xl bg-[var(--color-surface)] p-7"
          style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}
        >
          <h1 className="text-xl font-semibold tracking-tight text-[var(--color-text)]">
            Couldn't load your workspaces
          </h1>
          <p className="mt-2 text-sm text-[var(--color-text-muted)]">
            The app couldn&apos;t reach the server or read your organization memberships. Retry after
            checking network, API health, and CORS — this is not the same as having no organization.
          </p>
          <p className="mt-4 rounded-lg border border-[rgba(255,59,48,0.25)] bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger)]">
            {workspaceLoadError}
          </p>
          <div className="mt-5 flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              className="btn btn-primary flex-1 py-2.5"
              onClick={() => void retryWorkspaceLoad()}
            >
              <RefreshCcw className="h-4 w-4" /> Retry
            </button>
            <button type="button" className="btn btn-ghost flex-1" onClick={() => signOut()}>
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  // No memberships after a successful load — no self-service org creation in-app for any role.
  if (memberships.length === 0) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-[var(--color-bg)] px-4 py-10">
        <div
          className="w-full max-w-lg rounded-2xl bg-[var(--color-surface)] p-7"
          style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}
        >
          <h1 className="text-xl font-semibold tracking-tight text-[var(--color-text)]">
            No workspace assigned
          </h1>
          <p className="mt-2 text-sm text-[var(--color-text-muted)]">
            This account ({session.user.email}) is not a member of any organization yet. Workspace setup
            is disabled here — a{" "}
            <strong className="text-[var(--color-text)]">platform Dataly admin</strong> must add you via{" "}
            <strong className="text-[var(--color-text)]">Organization → Members</strong>,{" "}
            <strong className="text-[var(--color-text)]">Create user</strong>, or SQL{" "}
            <span className="font-mono text-[11px] text-[var(--color-text-subtle)]">
              organization_members
            </span>
            .
          </p>
          <p className="mt-3 text-xs text-[var(--color-text-subtle)]">
            New tenants still register via the public sign-up flow if enabled on your deployment.
          </p>
          <div className="mt-6 flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              className="btn btn-secondary flex-1"
              onClick={() => void retryWorkspaceLoad()}
            >
              <RefreshCcw className="h-4 w-4" /> Refresh memberships
            </button>
            <button type="button" className="btn btn-ghost flex-1" onClick={() => signOut()}>
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  const titles = SECTION_TITLES[section];

  const sidebarOrgs = memberships
    .map((m) => m.organizations)
    .filter((o): o is Org => !!o)
    .map((o) => ({
      id: o.id,
      name: o.active === false ? `${o.name} (inactive)` : o.name,
    }));

  const userDisplay =
    members.find((m) => m.user_id === session.user.id)?.profiles?.display_name ??
    session.user.email;

  return (
    <>
    <div className="flex h-dvh overflow-hidden bg-[var(--color-bg)]">
      <Sidebar
        active={section}
        onChange={setSection}
        orgs={sidebarOrgs}
        orgId={orgId}
        onOrgChange={setOrgId}
        user={{ email: session.user.email, displayName: userDisplay }}
        roleLabel={currentRole ? orgRoleLabel(currentRole) : null}
        onSignOut={signOut}
        showVaultSecrets={tenantSecretsUi}
        showOrganizationNav={showOrganizationChrome}
        workspacePicker={showOrganizationChrome}
        showAudit={canViewAuditLogs}
        showAdminProfile={canViewAuditLogs}
        showApiKeys={canManageTenantVaults}
        showDirectory={isPlatformAdmin}
        showDatabase={isPlatformAdmin}
        showSecurity={true}
        showWebhooks={canManageTenantVaults || isPlatformAdmin}
        showApprovals={tenantSecretsUi}
        showRotation={canManageTenantVaults || isPlatformAdmin}
      />

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <Topbar
          title={titles.title}
          subtitle={titles.subtitle}
          badge={
            currentRole && (
              <span className="chip chip-blue">{orgRoleLabel(currentRole)}</span>
            )
          }
        />

        <main className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-5 sm:p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.18 }}
            className="mx-auto max-w-6xl"
          >
            {error && (
              <div className="mb-5 rounded-lg border border-[rgba(255,59,48,0.25)] bg-[var(--color-danger-bg)] px-4 py-3 text-sm text-[var(--color-danger)]">
                {error}
              </div>
            )}

            {currentRole === "client_staff" && (
              <div
                className="mb-5 flex items-start gap-3 rounded-xl border px-4 py-3 text-sm"
                style={{
                  borderColor: "var(--color-border-blue-tint)",
                  background: "var(--color-accent-tint-bg)",
                  color: "var(--color-text)",
                }}
              >
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-accent)]" />
                <div className="min-w-0">
                  <p className="font-medium">
                    You're signed in as <strong>Client staff</strong>.
                  </p>
                  <p className="mt-0.5 text-[var(--color-text-muted)]">
                    Your <strong>Client admin</strong> manages secrets, API keys, and membership.
                    You can read or write secrets only when they grant access via{" "}
                    <strong>Secrets → Staff access</strong>.
                  </p>
                </div>
              </div>
            )}

            {currentRole === "public_user" && (
              <div
                className="mb-5 flex items-start gap-3 rounded-xl border px-4 py-3 text-sm"
                style={{
                  borderColor: "var(--color-border-blue-tint)",
                  background: "var(--color-accent-tint-bg)",
                  color: "var(--color-text)",
                }}
              >
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-accent)]" />
                <div className="min-w-0">
                  <p className="font-medium">
                    You're signed in as <strong>Public</strong> (self sign-up).
                  </p>
                  <p className="mt-0.5 text-[var(--color-text-muted)]">
                    A <strong>Client admin</strong> for this workspace can add you as{" "}
                    <strong>Client staff</strong> by email so you get secret access. A platform{" "}
                    <strong>Dataly admin</strong> can promote your role from their admin tools.
                  </p>
                </div>
              </div>
            )}

            {section === "overview" && (
              <OverviewSection
                secrets={secrets}
                members={members}
                auditLogs={auditLogs}
                onJump={setSection}
                isPlatformAdmin={isPlatformAdmin}
                tenantSecretsUi={tenantSecretsUi}
                canViewAuditLogs={canViewAuditLogs}
                canManageTenantVaults={canManageTenantVaults}
                orgName={
                  memberships.find((m) => m.organization_id === tenantDataOrgId)?.organizations
                    ?.name ??
                  (tenantDataOrgId ? `Organization (${tenantDataOrgId.slice(0, 8)}…)` : "Workspace")
                }
              />
            )}

            {section === "secrets" && tenantSecretsUi && tenantDataOrgId && (
              <SecretsSection
                orgId={tenantDataOrgId}
                vaults={vaults}
                secrets={secrets}
                vaultForSecret={vaultForSecret}
                setVaultForSecret={setVaultForSecret}
                secretName={secretName}
                setSecretName={setSecretName}
                secretPlain={secretPlain}
                setSecretPlain={setSecretPlain}
                secretFilters={secretFilters}
                setSecretFilters={setSecretFilters}
                draftServerQ={draftServerQ}
                setDraftServerQ={setDraftServerQ}
                draftTagTerms={draftTagTerms}
                setDraftTagTerms={setDraftTagTerms}
                localNameFilter={localNameFilter}
                setLocalNameFilter={setLocalNameFilter}
                reloadSecrets={async () => {
                  await loadOrgData();
                  await fetchAudit();
                }}
                members={members}
                canManageVaults={canManageTenantVaults}
                aclUser={aclUser}
                setAclUser={setAclUser}
                aclVault={aclVault}
                setAclVault={setAclVault}
                aclPerm={aclPerm}
                setAclPerm={setAclPerm}
                grantAcl={grantAcl}
                showToast={showToast}
              />
            )}

            {section === "organization" &&
              (currentRole === "dataly_admin" ? (
                <PlatformOrganizationSection
                  detailOrgId={platformOrgDetailMatch?.params.orgId ?? null}
                  onCreate={
                    canProvisionOrganizations
                      ? () => setPlatformCreateOrgOpen(true)
                      : undefined
                  }
                />
              ) : (
                <MembersSection
                  members={members}
                  canInviteStaff={canManageTenantVaults}
                  vaults={vaults}
                  inviteEmail={staffInviteEmail}
                  setInviteEmail={setStaffInviteEmail}
                  inviteVaultIds={staffInviteVaultIds}
                  setInviteVaultIds={setStaffInviteVaultIds}
                  inviteVaultPerm={staffInviteVaultPerm}
                  setInviteVaultPerm={setStaffInviteVaultPerm}
                  inviteOk={staffInviteOk}
                  onInviteStaff={inviteStaff}
                  inviteSubmitting={staffInviteSubmitting}
                  detailMemberUserId={tenantMemberDetailMatch?.params.userId ?? null}
                  workspaceName={
                    memberships.find((m) => m.organization_id === orgId)?.organizations?.name ??
                    "Workspace"
                  }
                />
              ))}

            {section === "api-keys" && canManageTenantVaults && (
              <ApiKeysSection
                edgeBase={edgeBase}
                apiKeys={apiKeys}
                apiKeyName={apiKeyName}
                setApiKeyName={setApiKeyName}
                revealedKey={revealedKey}
                handleCreateApiKey={handleCreateApiKey}
                revokeKey={revokeKey}
              />
            )}

            {section === "audit" && canViewAuditLogs && tenantDataOrgId && (
              <AuditSection
                orgId={tenantDataOrgId}
                auditQuery={auditQuery}
                auditLogs={auditLogs}
                auditTotal={auditTotal}
                auditDraft={auditDraft}
                setAuditDraft={setAuditDraft}
                setAuditQuery={setAuditQuery}
                fetchAudit={fetchAudit}
                showToast={showToast}
              />
            )}

            {section === "admin-profile" && canViewAuditLogs && (
              <AdminProfileSection
                loginEmail={session.user.email}
                organizationId={tenantDataOrgId}
                showWorkspaceSmtp={Boolean(
                  tenantDataOrgId && (currentRole === "client_admin" || isPlatformAdmin),
                )}
                showPlatformSmtp={isPlatformAdmin}
              />
            )}

            {section === "settings" && (
              <SettingsSection
                edgeBase={edgeBase}
                orgName={
                  memberships.find((m) => m.organization_id === orgId)?.organizations?.name ??
                  "Workspace"
                }
                orgSlug={
                  memberships.find((m) => m.organization_id === orgId)?.organizations?.slug ??
                  "—"
                }
                email={session.user.email}
                roleLabel={currentRole ? orgRoleLabel(currentRole) : "—"}
              />
            )}

            {section === "database" && isPlatformAdmin && <DatabaseSection />}
            {section === "database" && !isPlatformAdmin && (
              <Card title="Restricted">
                <EmptyState>
                  Only platform <strong>Dataly Admin</strong> users can browse the database.
                </EmptyState>
              </Card>
            )}

            {section === "directory" && isPlatformAdmin && (
              <PlatformUsersSection
                currentUserId={session.user.id}
                detailUserId={directoryDetailMatch?.params.userId ?? null}
                onCreate={() => setPlatformCreateUserOpen(true)}
              />
            )}
            {section === "directory" && !isPlatformAdmin && (
              <Card title="Restricted">
                <EmptyState>
                  Only platform <strong>Dataly Admin</strong> users can manage every account.
                </EmptyState>
              </Card>
            )}

            {section === "security" && (
              <SecuritySection
                mfaEnabled={Boolean(session.mfa?.enabled)}
                mfaRequiredForAll={Boolean(session.mfa?.required_for_all)}
                recoveryRemaining={0}
                onMfaChanged={() => {
                  // No-op: SPA refetches /auth/session on next route change
                  // because App.tsx watches `location.pathname`.
                }}
              />
            )}

            {section === "webhooks" && tenantDataOrgId && (canManageTenantVaults || isPlatformAdmin) && (
              <WebhooksSection orgId={tenantDataOrgId} />
            )}
            {section === "webhooks" && (!tenantDataOrgId || !(canManageTenantVaults || isPlatformAdmin)) && (
              <Card title="Restricted">
                <EmptyState>
                  Only <strong>Client admin</strong> or <strong>Dataly admin</strong> users can
                  configure webhooks.
                </EmptyState>
              </Card>
            )}

            {section === "approvals" && tenantDataOrgId && (
              <ApprovalsSection
                orgId={tenantDataOrgId}
                currentUserId={session.user.id}
                canApprove={canManageTenantVaults || isPlatformAdmin}
              />
            )}

            {section === "rotation" && tenantDataOrgId && (canManageTenantVaults || isPlatformAdmin) && (
              <RotationSection orgId={tenantDataOrgId} />
            )}
            {section === "rotation" && (!tenantDataOrgId || !(canManageTenantVaults || isPlatformAdmin)) && (
              <Card title="Restricted">
                <EmptyState>
                  Only <strong>Client admin</strong> or <strong>Dataly admin</strong> users can
                  manage rotation jobs.
                </EmptyState>
              </Card>
            )}
          </motion.div>
        </main>
      </div>
    </div>

    <ToastBanner toast={toast} onDismiss={() => setToast(null)} />

    <PlatformCreateOrgModal
      open={platformCreateOrgOpen}
      onClose={() => setPlatformCreateOrgOpen(false)}
      onCreated={async (created) => {
        await loadMembershipsCb();
        setOrgId(created.id);
      }}
    />
    <PlatformCreateUserModal
      open={platformCreateUserOpen}
      onClose={() => setPlatformCreateUserOpen(false)}
      workspaceOrgId={tenantDataOrgId}
      onSuccess={() => {
        void loadOrgData();
        void fetchAudit();
      }}
    />
    </>
  );
}

/* ─────────── shared primitives ─────────── */

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 flex items-baseline justify-between text-[0.78rem] font-medium text-[var(--color-text)]">
        <span>{label}</span>
        {hint && (
          <span className="text-[11px] font-normal text-[var(--color-text-subtle)]">{hint}</span>
        )}
      </span>
      {children}
    </label>
  );
}

function Card({
  title,
  description,
  action,
  children,
  className,
}: {
  title?: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("card overflow-hidden", className)}>
      {(title || action) && (
        <div className="flex flex-col gap-2 border-b border-[var(--color-border)] bg-[var(--color-surface)] px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            {title && (
              <h2 className="truncate text-[0.95rem] font-semibold text-[var(--color-text)]">
                {title}
              </h2>
            )}
            {description && (
              <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">{description}</p>
            )}
          </div>
          {action && <div className="shrink-0">{action}</div>}
        </div>
      )}
      <div className="px-5 py-4">{children}</div>
    </div>
  );
}

function StatTile({
  label,
  value,
  icon: Icon,
  accent,
}: {
  label: string;
  value: string | number;
  icon: typeof KeyRound;
  accent?: string;
}) {
  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-2)] p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <Icon
          className="h-5 w-5 shrink-0"
          style={{ color: accent ?? "var(--color-accent)" }}
        />
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--color-text-subtle)]">
            {label}
          </p>
          <p
            className="mt-1.5 text-2xl font-semibold tracking-tight"
            style={{ color: accent ?? "var(--color-text)" }}
          >
            {value}
          </p>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ children }: { children: ReactNode }) {
  return (
    <p className="py-6 text-center text-sm text-[var(--color-text-subtle)]">{children}</p>
  );
}

/* ─────────── sections ─────────── */

function OverviewSection({
  secrets,
  members,
  auditLogs,
  orgName,
  onJump,
  isPlatformAdmin,
  tenantSecretsUi,
  canViewAuditLogs,
  canManageTenantVaults,
}: {
  secrets: SecretRow[];
  members: MemberRow[];
  auditLogs: AuditLogRow[];
  orgName: string;
  onJump: (k: NavKey) => void;
  isPlatformAdmin: boolean;
  tenantSecretsUi: boolean;
  canViewAuditLogs: boolean;
  canManageTenantVaults: boolean;
}) {
  const quickActions: {
    k: NavKey;
    label: string;
    desc: string;
    icon: ComponentType<{ className?: string }>;
  }[] = [
    ...(tenantSecretsUi
      ? ([
          {
            k: "secrets" as const,
            label: "Add a secret",
            desc: "Store an encrypted value",
            icon: KeyRound,
          },
          ...(canManageTenantVaults
            ? ([
                {
                  k: "api-keys" as const,
                  label: "Generate API key",
                  desc: "For CI / automation",
                  icon: Code2,
                },
              ] as const)
            : []),
        ] as const)
      : []),
    ...(canViewAuditLogs
      ? ([
          {
            k: "audit" as const,
            label: "Review audit",
            desc: "See who did what",
            icon: ListChecks,
          },
        ] as const)
      : []),
    ...(isPlatformAdmin
      ? ([
          {
            k: "directory" as NavKey,
            label: "All users",
            desc: "Deactivate, reset passwords, edit profiles",
            icon: UserRoundSearch,
          },
        ] as const)
      : []),
  ];

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <StatTile label="Workspace" value={orgName} icon={Building2} accent="#1d1d1f" />
        <StatTile label="Secrets" value={secrets.length} icon={KeyRound} accent="#5856d6" />
        <StatTile label="Members" value={members.length} icon={UsersIcon} accent="#34c759" />
      </div>

      <div
        className={cn(
          "grid grid-cols-1 gap-5",
          canViewAuditLogs && "lg:grid-cols-2",
        )}
      >
        <Card
          title="Quick actions"
          description={
            quickActions.length === 0
              ? "No shortcuts for this role — open Members if you need the team list."
              : undefined
          }
        >
          {quickActions.length === 0 ? (
            <EmptyState>No shortcuts available.</EmptyState>
          ) : (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {quickActions.map((a) => (
                <button
                  key={a.k}
                  type="button"
                  onClick={() => onJump(a.k)}
                  className="flex items-center gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] p-3 text-left transition hover:border-[rgba(0,122,255,0.3)] hover:bg-[var(--color-surface)]"
                >
                  <a.icon className="h-5 w-5 text-[var(--color-accent)]" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[var(--color-text)]">{a.label}</p>
                    <p className="text-xs text-[var(--color-text-muted)]">{a.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </Card>

        {canViewAuditLogs && (
          <Card title="Recent activity" description="Latest 10 events on this workspace.">
            <ul className="divide-y divide-[var(--color-border)]">
              {auditLogs.slice(0, 10).map((a) => (
                <li key={a.id} className="flex items-start gap-3 py-2.5 text-sm">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--color-accent)]" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-mono text-[12px] text-[var(--color-text)]">
                      {a.action}
                    </p>
                    <p className="truncate text-[11px] text-[var(--color-text-subtle)]">
                      {a.actor_email ?? a.actor_type} ·{" "}
                      {new Date(a.created_at).toLocaleString()}
                    </p>
                  </div>
                </li>
              ))}
              {auditLogs.length === 0 && (
                <EmptyState>
                  No activity yet — create or update a secret to see events here.
                </EmptyState>
              )}
            </ul>
          </Card>
        )}
      </div>
    </div>
  );
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard may be blocked */
    }
  }
  return (
    <button
      type="button"
      onClick={copy}
      className="rounded-md p-1 text-[var(--color-text-muted)] transition hover:bg-[var(--color-surface-3)] hover:text-[var(--color-text)]"
      aria-label={copied ? "Copied" : "Copy"}
      title={copied ? "Copied" : "Copy"}
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-[var(--color-success)]" />
      ) : (
        <Copy className="h-3.5 w-3.5" />
      )}
    </button>
  );
}

function adminCreateUserMessage(code: string): string {
  switch (code) {
    case "invalid_email":
      return "Use an email address with text before and after the @ sign.";
    case "email_taken":
      return "That email is already registered.";
    case "email_not_configured":
      return "Outbound email isn’t configured. Open Admin profile → SMTP relay (workspace or platform), set API SMTP_* env vars, or use STAFF_PROVISION_RETURN_PASSWORD=1 for local dev only.";
    case "invalid_organization":
      return "Pick a valid workspace from the organization list.";
    case "organization_required":
      return "Choose which organization this user belongs to.";
    case "organization_inactive":
      return "That workspace is deactivated — activate it on the Organization tab first.";
    case "invalid_payload":
      return "Display name is required.";
    default:
      return code || "Could not create user.";
  }
}

function PlatformCreateOrgModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  /** After a successful create — reload memberships and select the new org for client admins. */
  onCreated?: (organization: { id: string; name: string; slug: string }) => void | Promise<void>;
}) {
  const [newOrgName, setNewOrgName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setNewOrgName("");
    setErr(null);
    setBusy(false);
  }, [open]);

  if (!open) return null;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const name = newOrgName.trim();
    if (!name) return;
    setBusy(true);
    setErr(null);
    try {
      const created = await adminCreateOrganization(name);
      await onCreated?.(created);
      setNewOrgName("");
      onClose();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed";
      setErr(
        msg === "slug_collision"
          ? "Could not allocate a unique slug. Try a slightly different name."
          : msg === "duplicate_organization"
            ? "An organization with this name already exists. Try a different name."
            : msg === "organization_create_forbidden"
              ? "Only a Dataly admin or a client admin can create organizations. If you were promoted recently, sign out and sign back in. Deactivated workspaces no longer block creating a new tenant."
              : msg === "account_disabled"
                ? "Your account is disabled. Ask an administrator to reactivate it."
                : msg === "forbidden" || msg === "Forbidden"
                  ? "Access denied (403). Confirm you are signed in as a Dataly admin or client admin; try signing out and back in."
                  : msg,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 px-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="platform-create-org-title"
      onClick={(ev) => {
        if (ev.target === ev.currentTarget) onClose();
      }}
    >
      <div
        className="relative w-full max-w-lg rounded-2xl bg-[var(--color-surface)] p-6"
        style={{ boxShadow: "var(--shadow-modal)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="absolute right-4 top-4 rounded-md p-1 text-[var(--color-text-muted)] transition hover:bg-[var(--color-surface-3)] hover:text-[var(--color-text)]"
          onClick={onClose}
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
        <h2
          id="platform-create-org-title"
          className="pr-10 text-lg font-semibold text-[var(--color-text)]"
        >
          Create organization
        </h2>
        <p className="mt-1 text-xs text-[var(--color-text-muted)]">
          Adds a new tenant with a default secret store. Slug is generated from the name (short suffix if
          taken). Platform admins create empty tenants; if you are a client admin, you are added as Client
          admin on the new workspace.
        </p>
        {err && (
          <p className="mt-4 rounded-lg border border-[rgba(255,59,48,0.25)] bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger)]">
            {err}
          </p>
        )}
        <form onSubmit={onSubmit} className="mt-4 flex flex-col gap-3">
          <Field label="Organization name">
            <input
              className="field-input"
              placeholder="e.g. Acme Engineering"
              value={newOrgName}
              onChange={(e) => setNewOrgName(e.target.value)}
              disabled={busy}
              autoFocus
            />
          </Field>
          {newOrgName.trim() ? (
            <p className="font-mono text-[11px] text-[var(--color-text-subtle)]">
              Slug preview:{" "}
              <span className="text-[var(--color-text-muted)]">{slugifyOrgNamePreview(newOrgName)}</span>
              <span className="text-[var(--color-text-subtle)]"> …</span>
            </p>
          ) : null}
          <div className="flex flex-wrap gap-2 pt-1">
            <button type="submit" className="btn btn-primary" disabled={busy || !newOrgName.trim()}>
              <Building2 className="h-4 w-4" /> Create
            </button>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PlatformCreateUserModal({
  open,
  onClose,
  workspaceOrgId,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  /** Default workspace row when it exists in the loaded org list */
  workspaceOrgId: string | null;
  onSuccess: () => void | Promise<void>;
}) {
  const [cuEmail, setCuEmail] = useState("");
  const [cuName, setCuName] = useState("");
  const [cuPhone, setCuPhone] = useState("");
  const [createUserOrgs, setCreateUserOrgs] = useState<AdminOrgRow[]>([]);
  const [loadingCreateUserOrgs, setLoadingCreateUserOrgs] = useState(false);
  const [selectedCreateOrgId, setSelectedCreateOrgId] = useState("");
  const [orgPickerQuery, setOrgPickerQuery] = useState("");
  const [cuBusy, setCuBusy] = useState(false);
  const [cuErr, setCuErr] = useState<string | null>(null);
  const [cuOk, setCuOk] = useState<string | null>(null);
  const [cuTempPassword, setCuTempPassword] = useState<string | null>(null);

  const optionsForCreateUserOrg = useMemo(() => {
    const q = orgPickerQuery.trim().toLowerCase();
    const base = q
      ? createUserOrgs.filter(
          (o) => o.name.toLowerCase().includes(q) || o.slug.toLowerCase().includes(q),
        )
      : createUserOrgs;
    const sel = selectedCreateOrgId;
    if (sel && !base.some((o) => o.id === sel)) {
      const hit = createUserOrgs.find((o) => o.id === sel);
      return hit ? [hit, ...base] : base;
    }
    return base;
  }, [createUserOrgs, orgPickerQuery, selectedCreateOrgId]);

  useEffect(() => {
    if (!open) return;
    setCuErr(null);
    setCuOk(null);
    setCuTempPassword(null);
    setCuEmail("");
    setCuName("");
    setCuPhone("");
    setOrgPickerQuery("");
    let cancelled = false;
    (async () => {
      setLoadingCreateUserOrgs(true);
      try {
        const list = await adminListOrgs();
        if (cancelled) return;
        setCreateUserOrgs(list);
        const preferred =
          workspaceOrgId && list.some((o) => o.id === workspaceOrgId)
            ? workspaceOrgId
            : list.find((o) => o.active ?? true)?.id ?? "";
        setSelectedCreateOrgId(preferred);
      } catch (e) {
        if (!cancelled) {
          setCreateUserOrgs([]);
          setSelectedCreateOrgId("");
          setCuErr(e instanceof Error ? e.message : "Failed to load organizations");
        }
      } finally {
        if (!cancelled) setLoadingCreateUserOrgs(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, workspaceOrgId]);

  if (!open) return null;

  async function submitCreateUser(e: FormEvent) {
    e.preventDefault();
    setCuErr(null);
    setCuOk(null);
    setCuTempPassword(null);
    if (!selectedCreateOrgId) {
      setCuErr("Choose which organization this user belongs to.");
      return;
    }
    const orgMeta = createUserOrgs.find((o) => o.id === selectedCreateOrgId);
    if (!(orgMeta?.active ?? true)) {
      setCuErr(adminCreateUserMessage("organization_inactive"));
      return;
    }
    setCuBusy(true);
    try {
      const createdEmail = cuEmail.trim().toLowerCase();
      const result = await adminCreateUser({
        email: cuEmail,
        full_name: cuName,
        phone: cuPhone,
        organization_id: selectedCreateOrgId,
      });
      const label = orgMeta?.name ?? "workspace";
      const bits = [
        `Created ${createdEmail} and added them to “${label}” as Client admin.`,
      ];
      if (result.email_sent) {
        bits.push("Welcome email with sign-in link and temporary password was sent.");
        if (result.temporary_password) bits.push("Temporary password is also shown below.");
      } else if (result.temporary_password) {
        bits.push("Email was not sent — copy the temporary password below for this user.");
      } else {
        bits.push("Email could not be sent — check server SMTP settings.");
      }
      setCuOk(bits.join(" "));
      setCuTempPassword(result.temporary_password ?? null);
      setCuEmail("");
      setCuName("");
      setCuPhone("");
      await onSuccess();
    } catch (err) {
      const code = err instanceof Error ? err.message : String(err);
      setCuErr(adminCreateUserMessage(code));
    } finally {
      setCuBusy(false);
    }
  }

  const canSubmit =
    cuEmail.trim() &&
    cuName.trim() &&
    selectedCreateOrgId &&
    !loadingCreateUserOrgs &&
    !cuBusy;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 px-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="platform-create-user-title"
      onClick={(ev) => {
        if (ev.target === ev.currentTarget) onClose();
      }}
    >
      <div
        className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-[var(--color-surface)] p-6"
        style={{ boxShadow: "var(--shadow-modal)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="absolute right-4 top-4 rounded-md p-1 text-[var(--color-text-muted)] transition hover:bg-[var(--color-surface-3)] hover:text-[var(--color-text)]"
          onClick={onClose}
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
        <h2
          id="platform-create-user-title"
          className="pr-10 text-lg font-semibold text-[var(--color-text)]"
        >
          Create user
        </h2>
        <p className="mt-1 text-xs text-[var(--color-text-muted)]">
          Choose the organization by name. A temporary password is generated on the server and emailed to the
          user. The account is added as{" "}
          <strong className="text-[var(--color-text)]">Client admin</strong> for that workspace only.
        </p>
        {cuErr && (
          <p className="mt-4 rounded-lg border border-[rgba(255,59,48,0.25)] bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger)]">
            {cuErr}
          </p>
        )}
        {cuOk && (
          <div
            className="mt-4 rounded-lg px-3 py-2 text-sm"
            style={{
              border: "1px solid rgba(52,199,89,0.25)",
              background: "var(--color-success-bg)",
              color: "#1f8b3a",
            }}
          >
            <p>{cuOk}</p>
            {cuTempPassword ? (
              <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md bg-[var(--color-surface)] px-2 py-1.5 font-mono text-[var(--color-text)]">
                <span className="break-all text-xs">{cuTempPassword}</span>
                <CopyButton value={cuTempPassword} />
              </div>
            ) : null}
          </div>
        )}
        <form onSubmit={submitCreateUser} className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Field label="Email">
              <input
                className="field-input"
                type="text"
                inputMode="email"
                autoComplete="email"
                placeholder="any.valid@address"
                value={cuEmail}
                onChange={(e) => setCuEmail(e.target.value)}
                disabled={cuBusy}
                autoFocus
              />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Display name">
              <input
                className="field-input"
                autoComplete="name"
                placeholder="Full name"
                value={cuName}
                onChange={(e) => setCuName(e.target.value)}
                disabled={cuBusy}
              />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Phone (optional)">
              <input
                className="field-input"
                type="text"
                autoComplete="tel"
                value={cuPhone}
                onChange={(e) => setCuPhone(e.target.value)}
                disabled={cuBusy}
              />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field
              label="Organization"
              hint={loadingCreateUserOrgs ? "Loading…" : `${createUserOrgs.length} workspaces`}
            >
              <div className="flex flex-col gap-2">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-subtle)]" />
                  <input
                    type="search"
                    className="field-input w-full py-2 pl-9 pr-3 text-sm"
                    placeholder="Filter by organization name or slug…"
                    value={orgPickerQuery}
                    onChange={(e) => setOrgPickerQuery(e.target.value)}
                    disabled={cuBusy || loadingCreateUserOrgs || createUserOrgs.length === 0}
                    aria-label="Filter organizations"
                  />
                </div>
                <select
                  className="field-input text-sm"
                  value={selectedCreateOrgId}
                  onChange={(e) => setSelectedCreateOrgId(e.target.value)}
                  disabled={cuBusy || loadingCreateUserOrgs || createUserOrgs.length === 0}
                  required
                  aria-label="Organization for new user"
                >
                  {createUserOrgs.length === 0 && !loadingCreateUserOrgs ? (
                    <option value="">No organizations — create one from Overview</option>
                  ) : (
                    <>
                      <option value="">Select organization…</option>
                      {optionsForCreateUserOrg.map((o) => (
                        <option
                          key={o.id}
                          value={o.id}
                          disabled={!(o.active ?? true)}
                        >
                          {o.name} ({o.slug})
                          {!(o.active ?? true) ? " — inactive" : ""}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </div>
            </Field>
          </div>
          <div className="flex flex-wrap gap-2 sm:col-span-2">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!canSubmit}
            >
              <UserPlus className="h-4 w-4" /> Create user
            </button>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={cuBusy}>
              {cuOk ? "Close" : "Cancel"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** Human text for cross-tenant admin API failures (403 platform gate, etc.). */
function platformOrganizationSectionErr(e: unknown): string {
  const msg = e instanceof Error ? e.message : "Failed";
  if (msg === "organization_inactive") return "That workspace is deactivated.";
  if (msg === "user_not_found") return "That user is not registered.";
  if (msg === "platform_admin_required" || msg === "forbidden") {
    return "The server denied this action because your account is not recognized as a platform Dataly admin, or your session is out of date. Sign out and sign back in. If it persists, ask an operator to confirm you have role dataly_admin in organization_members. With a reverse proxy or CDN, ensure DELETE requests forward the Authorization header.";
  }
  return msg;
}

function PlatformOrganizationSection({
  detailOrgId,
  onCreate,
}: {
  detailOrgId: string | null;
  onCreate?: () => void;
}) {
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState<AdminOrgRow[]>([]);
  const [clientAdmins, setClientAdmins] = useState<AdminClientAdminRow[]>([]);
  const [choices, setChoices] = useState<Record<string, string>>({});
  const [members, setMembers] = useState<AdminMemberRow[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [busyAssignOrgId, setBusyAssignOrgId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [addEmail, setAddEmail] = useState("");
  const [addRole, setAddRole] = useState<ApiOrgRole>("public_user");
  const [adminOrgSearch, setAdminOrgSearch] = useState("");
  const [orgRecTab, setOrgRecTab] = useState<"details" | "members">("details");
  const [orgListSel, setOrgListSel] = useState<Set<string>>(new Set());
  const detailOrgIdRef = useRef<string | null>(null);

  useEffect(() => {
    detailOrgIdRef.current = detailOrgId;
  }, [detailOrgId]);

  useEffect(() => {
    setOrgRecTab("details");
  }, [detailOrgId]);

  const reloadOrgs = useCallback(async () => {
    try {
      const [list, ca] = await Promise.all([adminListOrgs(), adminListClientAdminUsers()]);
      setOrgs(list);
      setClientAdmins(ca);
      const cur = detailOrgIdRef.current;
      if (cur && !list.some((o) => o.id === cur)) navigate("/app", { replace: true });
    } catch (e) {
      setErr(platformOrganizationSectionErr(e));
    }
  }, [navigate]);

  const reloadMembers = useCallback(async (orgId: string) => {
    try {
      const m = await adminListOrgMembers(orgId);
      setMembers(m);
    } catch (e) {
      setErr(platformOrganizationSectionErr(e));
    }
  }, []);

  const filteredAdminOrgs = useMemo(() => {
    const q = adminOrgSearch.trim().toLowerCase();
    if (!q) return orgs;
    return orgs.filter(
      (o) => o.name.toLowerCase().includes(q) || o.slug.toLowerCase().includes(q),
    );
  }, [orgs, adminOrgSearch]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        await reloadOrgs();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadOrgs]);

  useEffect(() => {
    if (!detailOrgId) {
      setMembers([]);
      setMembersLoading(false);
      return;
    }
    let cancelled = false;
    setMembersLoading(true);
    setMembers([]);
    (async () => {
      try {
        await reloadMembers(detailOrgId);
      } catch {
        if (!cancelled) setMembers([]);
      } finally {
        if (!cancelled) setMembersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [detailOrgId, reloadMembers]);

  useEffect(() => {
    if (!detailOrgId) return;
    setAddEmail("");
    setAddRole("public_user");
  }, [detailOrgId]);

  async function refreshAll() {
    setBusy(true);
    setErr(null);
    try {
      await reloadOrgs();
      const mid = detailOrgIdRef.current;
      if (mid) await reloadMembers(mid);
    } catch (e) {
      setErr(platformOrganizationSectionErr(e));
    } finally {
      setBusy(false);
    }
  }

  async function assign(org: AdminOrgRow) {
    const email = (choices[org.id] ?? "").trim().toLowerCase();
    if (!email || !org.active) return;
    setBusyAssignOrgId(org.id);
    setErr(null);
    try {
      await adminAddMember(org.id, email, "client_admin");
      setChoices((prev) => ({ ...prev, [org.id]: "" }));
      await reloadOrgs();
      if (detailOrgIdRef.current === org.id) await reloadMembers(org.id);
    } catch (e) {
      setErr(platformOrganizationSectionErr(e));
    } finally {
      setBusyAssignOrgId(null);
    }
  }

  async function changeRole(userId: string, role: ApiOrgRole) {
    if (!detailOrgId) return;
    setBusy(true);
    setErr(null);
    try {
      await adminSetMemberRole(detailOrgId, userId, role);
      await reloadMembers(detailOrgId);
      await reloadOrgs();
    } catch (e) {
      setErr(platformOrganizationSectionErr(e));
    } finally {
      setBusy(false);
    }
  }

  async function addMember(e: FormEvent) {
    e.preventDefault();
    if (!detailOrgId || !addEmail.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await adminAddMember(detailOrgId, addEmail.trim().toLowerCase(), addRole);
      setAddEmail("");
      await reloadMembers(detailOrgId);
      await reloadOrgs();
    } catch (e) {
      setErr(platformOrganizationSectionErr(e));
    } finally {
      setBusy(false);
    }
  }

  async function removeMember(userId: string) {
    if (!detailOrgId) return;
    setBusy(true);
    setErr(null);
    try {
      await adminRemoveMember(detailOrgId, userId);
      await reloadMembers(detailOrgId);
      await reloadOrgs();
    } catch (e) {
      setErr(platformOrganizationSectionErr(e));
    } finally {
      setBusy(false);
    }
  }

  async function deactivateOrg(org: AdminOrgRow) {
    if (
      !window.confirm(
        `Deactivate "${org.name}"? Members and API keys lose access until you activate this tenant again.`,
      )
    )
      return;
    setBusy(true);
    setErr(null);
    try {
      await adminDeactivateOrganization(org.id);
      await reloadOrgs();
      if (detailOrgIdRef.current === org.id) await reloadMembers(org.id);
    } catch (e) {
      setErr(platformOrganizationSectionErr(e));
    } finally {
      setBusy(false);
    }
  }

  async function activateOrg(org: AdminOrgRow) {
    setBusy(true);
    setErr(null);
    try {
      await adminActivateOrganization(org.id);
      await reloadOrgs();
      if (detailOrgIdRef.current === org.id) await reloadMembers(org.id);
    } catch (e) {
      setErr(platformOrganizationSectionErr(e));
    } finally {
      setBusy(false);
    }
  }

  async function deleteOrg(org: AdminOrgRow) {
    const typed = window.prompt(
      `PERMANENT DELETE "${org.name}" — all secrets, stores, memberships, and API keys.\nType slug exactly to confirm: ${org.slug}`,
    );
    if ((typed ?? "").trim() !== org.slug) return;
    setBusy(true);
    setErr(null);
    try {
      await adminDeleteOrganization(org.id);
      await reloadOrgs();
      setMembers([]);
      if (detailOrgIdRef.current === org.id) navigate("/app", { replace: true });
    } catch (e) {
      setErr(platformOrganizationSectionErr(e));
    } finally {
      setBusy(false);
    }
  }

  if (loading && orgs.length === 0) {
    return (
      <Card title="Loading">
        <EmptyState>Fetching organizations…</EmptyState>
      </Card>
    );
  }

  const detailOrg =
    detailOrgId != null ? (orgs.find((o) => o.id === detailOrgId) ?? null) : null;

  const detailAssignSel = detailOrg ? (choices[detailOrg.id] ?? "") : "";
  const detailAssignBusy = detailOrg ? busyAssignOrgId === detailOrg.id : false;

  const filteredOrgIds = filteredAdminOrgs.map((o) => o.id);
  const allOrgRowsSelected =
    filteredOrgIds.length > 0 && filteredOrgIds.every((id) => orgListSel.has(id));

  function toggleOrgRow(id: string) {
    setOrgListSel((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleOrgSelectAllVisible() {
    setOrgListSel((prev) => {
      const next = new Set(prev);
      if (allOrgRowsSelected) filteredOrgIds.forEach((id) => next.delete(id));
      else filteredOrgIds.forEach((id) => next.add(id));
      return next;
    });
  }

  return (
    <div className="flex flex-col gap-5">
      {err && (
        <div className="rounded-lg border border-[rgba(255,59,48,0.25)] bg-[var(--color-danger-bg)] px-4 py-3 text-sm text-[var(--color-danger)]">
          {err}
        </div>
      )}

      {detailOrgId ? (
        <>
          {!detailOrg ? (
            <Card title="Organization">
              <EmptyState>
                No workspace matches this link. It may have been deleted or the URL is wrong.
              </EmptyState>
              <button
                type="button"
                className="btn btn-secondary mt-4"
                onClick={() => navigate("/app")}
              >
                <ChevronLeftIcon className="h-4 w-4" /> Back to organizations
              </button>
            </Card>
          ) : (
            <>
              <SfRecordHero
                icon={<Building2 className="h-6 w-6" />}
                objectLabel="Organization"
                recordTitle={detailOrg.name}
                subtitle={
                  <span className="font-mono text-[11px] text-[var(--color-text-subtle)]">
                    {detailOrg.record_id ?? detailOrg.id} · {detailOrg.slug}
                  </span>
                }
                highlights={[
                  {
                    label: "Status",
                    value: (
                      <span
                        className={cn(
                          "chip text-[11px]",
                          detailOrg.active ? "chip-blue" : "opacity-90",
                        )}
                        style={
                          detailOrg.active
                            ? undefined
                            : {
                                borderColor: "rgba(255,149,0,0.35)",
                                color: "#b45309",
                              }
                        }
                      >
                        {detailOrg.active ? "Active" : "Inactive"}
                      </span>
                    ),
                  },
                  { label: "Members", value: detailOrg.member_count },
                  { label: "Vaults", value: detailOrg.vault_count },
                  {
                    label: "Created",
                    value: new Date(detailOrg.created_at).toLocaleDateString(),
                  },
                  {
                    label: "Workspace ID",
                    value: (
                      <span className="break-all font-mono text-[11px]">{detailOrg.id}</span>
                    ),
                  },
                ]}
                alert={
                  !detailOrg.active ? (
                    <div
                      className="px-4 py-2 text-sm"
                      style={{
                        background: "rgba(255,149,0,0.08)",
                        color: "#92400e",
                      }}
                    >
                      Deactivated — tenant APIs and machine keys are blocked until you activate this
                      workspace again.
                    </div>
                  ) : undefined
                }
                actions={
                  <>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => navigate("/app")}
                    >
                      <ChevronLeftIcon className="h-4 w-4" /> Back to list
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => void refreshAll()}
                      disabled={busy || detailAssignBusy || loading}
                    >
                      <RefreshCcw className={cn("h-4 w-4", busy && "spin")} /> Refresh
                    </button>
                    {detailOrg.active ? (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={busy || detailAssignBusy}
                        onClick={() => void deactivateOrg(detailOrg)}
                      >
                        <Ban className="h-4 w-4" /> Deactivate
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-secondary text-[var(--color-success)]"
                        disabled={busy || detailAssignBusy}
                        onClick={() => void activateOrg(detailOrg)}
                      >
                        <CircleCheck className="h-4 w-4" /> Activate
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-secondary text-[var(--color-danger)]"
                      disabled={busy || detailAssignBusy}
                      onClick={() => void deleteOrg(detailOrg)}
                    >
                      <Trash2 className="h-4 w-4" /> Delete permanently
                    </button>
                  </>
                }
              />
              <SfSplitDetail
                tabs={[
                  { id: "details", label: "Details" },
                  { id: "members", label: "Members" },
                ]}
                tabActive={orgRecTab}
                onTabChange={(id) => setOrgRecTab(id as "details" | "members")}
                main={
                  orgRecTab === "details" ? (
                    <SfFieldGrid
                      rows={[
                        { label: "Organization name", value: detailOrg.name },
                        {
                          label: "Slug",
                          value: (
                            <span className="font-mono text-xs">{detailOrg.slug}</span>
                          ),
                        },
                        {
                          label: "Record ID",
                          value: (
                            <span className="font-mono text-xs">
                              {detailOrg.record_id ?? "—"}
                            </span>
                          ),
                        },
                        {
                          label: "Workspace ID",
                          value: (
                            <span className="break-all font-mono text-[11px]">{detailOrg.id}</span>
                          ),
                        },
                        {
                          label: "Status",
                          value: detailOrg.active ? "Active" : "Inactive",
                        },
                        { label: "Members", value: detailOrg.member_count },
                        { label: "Vaults", value: detailOrg.vault_count },
                        {
                          label: "Created",
                          value: new Date(detailOrg.created_at).toLocaleString(),
                        },
                      ]}
                    />
                  ) : (
                    <div className="flex flex-col gap-4">
                      {!detailOrg.active && (
                        <p
                          className="rounded-lg border px-3 py-2 text-sm"
                          style={{
                            borderColor: "rgba(255,149,0,0.35)",
                            background: "rgba(255,149,0,0.08)",
                            color: "#92400e",
                          }}
                        >
                          Deactivated organizations block tenant APIs and machine keys. You can still
                          change roles or remove members; activate the tenant to add users through this
                          form.
                        </p>
                      )}
                      <p className="text-xs text-[var(--color-text-muted)]">
                        Promote / demote users, or add a registered user to this org (must already have an
                        account).
                      </p>
                      <form
                        onSubmit={addMember}
                        className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr,auto,auto]"
                      >
                        <input
                          className="field-input"
                          placeholder="user@example.com (must already be registered)"
                          value={addEmail}
                          onChange={(e) => setAddEmail(e.target.value)}
                          type="text"
                          inputMode="email"
                          autoComplete="email"
                          disabled={busy || !detailOrg.active}
                        />
                        <select
                          className="field-input"
                          value={addRole}
                          onChange={(e) => setAddRole(e.target.value as ApiOrgRole)}
                          disabled={busy || !detailOrg.active}
                        >
                          <option value="public_user">Public</option>
                          <option value="client_staff">Client staff</option>
                          <option value="client_admin">Client admin</option>
                          <option value="dataly_admin">Dataly admin</option>
                        </select>
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={busy || !addEmail.trim() || !detailOrg.active}
                        >
                          <UserPlus className="h-4 w-4" /> Add to org
                        </button>
                      </form>
                      <SfTableWrap>
                        {membersLoading ? (
                          <EmptyState>Loading members…</EmptyState>
                        ) : members.length === 0 ? (
                          <EmptyState>No members in this organization.</EmptyState>
                        ) : (
                          <table className="dl-table">
                            <thead>
                              <tr>
                                <th>User</th>
                                <th>Email</th>
                                <th>Phone</th>
                                <th>Role</th>
                                <th>Joined</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {members.map((m) => (
                                <tr key={m.user_id}>
                                  <td className="font-medium text-[var(--color-text)]">
                                    {m.display_name ?? "—"}
                                  </td>
                                  <td className="text-xs text-[var(--color-text)]">{m.email}</td>
                                  <td className="text-xs text-[var(--color-text-muted)]">
                                    {m.phone ?? "—"}
                                  </td>
                                  <td>
                                    <select
                                      className="field-input py-1 text-xs"
                                      value={m.role}
                                      onChange={(e) =>
                                        changeRole(m.user_id, e.target.value as ApiOrgRole)
                                      }
                                      disabled={busy}
                                    >
                                      <option value="public_user">Public</option>
                                      <option value="client_staff">Client staff</option>
                                      <option value="client_admin">Client admin</option>
                                      <option value="dataly_admin">Dataly admin</option>
                                    </select>
                                  </td>
                                  <td className="text-xs text-[var(--color-text-subtle)]">
                                    {new Date(m.joined_at).toLocaleDateString()}
                                  </td>
                                  <td className="text-right">
                                    <button
                                      type="button"
                                      onClick={() => void removeMember(m.user_id)}
                                      className="btn btn-ghost px-2 py-1 text-[var(--color-danger)]"
                                      title="Remove from org"
                                      disabled={busy}
                                    >
                                      <UserMinus className="h-3.5 w-3.5" />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </SfTableWrap>
                    </div>
                  )
                }
                sidebar={
                  <>
                    <SfSidebarCard
                      title="Assign Client admin"
                      description={
                        clientAdmins.length === 0
                          ? "No Client admins yet in any workspace — add someone as Client admin from the Members tab."
                          : "Grant Client admin on this workspace to an existing Client admin from another tenant."
                      }
                    >
                      <div className="flex flex-col gap-2">
                        <select
                          className="field-input w-full py-2 text-sm"
                          value={detailAssignSel}
                          disabled={
                            !detailOrg.active ||
                            clientAdmins.length === 0 ||
                            detailAssignBusy ||
                            busy
                          }
                          onChange={(e) =>
                            setChoices((prev) => ({ ...prev, [detailOrg.id]: e.target.value }))
                          }
                          aria-label="Choose user to assign as Client admin"
                        >
                          <option value="">Select user…</option>
                          {clientAdmins.map((u) => (
                            <option key={u.id} value={u.email}>
                              {u.display_name ? `${u.display_name} (${u.email})` : u.email}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="btn btn-primary w-full"
                          disabled={
                            !detailOrg.active ||
                            !detailAssignSel ||
                            detailAssignBusy ||
                            busy ||
                            clientAdmins.length === 0
                          }
                          onClick={() => void assign(detailOrg)}
                        >
                          <UserPlus className="h-4 w-4" /> Assign
                        </button>
                      </div>
                    </SfSidebarCard>
                  </>
                }
              />
            </>
          )}
        </>
      ) : (
        <>
          <SfListShell
            objectLabel="Organizations"
            viewLabel="All workspaces"
            metaRight={`${filteredAdminOrgs.length} row${filteredAdminOrgs.length === 1 ? "" : "s"} shown · ${orgs.length} total`}
            actions={
              <div className="flex flex-wrap items-center gap-2">
                {onCreate ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={onCreate}
                    disabled={busy}
                  >
                    <Building2 className="h-4 w-4" /> Create organization
                  </button>
                ) : null}
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => void refreshAll()}
                  disabled={busy || busyAssignOrgId != null || loading}
                >
                  <RefreshCcw className={cn("h-4 w-4", busy && "spin")} /> Refresh
                </button>
              </div>
            }
            toolbar={
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="relative min-w-0 flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-subtle)]" />
                  <input
                    type="search"
                    className="field-input w-full py-2 pl-9 pr-3 text-sm"
                    placeholder="Search this list…"
                    value={adminOrgSearch}
                    onChange={(e) => setAdminOrgSearch(e.target.value)}
                    aria-label="Filter organizations by name or slug"
                  />
                </div>
              </div>
            }
          >
            {orgs.length === 0 ? (
              <div className="p-6">
                <EmptyState>
                  No organizations yet.
                  {onCreate ? " Use Create organization above to add the first one." : null}
                </EmptyState>
              </div>
            ) : filteredAdminOrgs.length === 0 ? (
              <div className="p-6">
                <EmptyState>No organizations match your search.</EmptyState>
              </div>
            ) : (
              <SfTableWrap>
                <table className="dl-table">
                  <thead>
                    <tr>
                      <th className="w-10 text-center text-[var(--color-text-subtle)]">#</th>
                      <th className="w-10">
                        <input
                          type="checkbox"
                          checked={allOrgRowsSelected}
                          onChange={() => toggleOrgSelectAllVisible()}
                          aria-label="Select all visible rows"
                          className="rounded border-[var(--color-border)]"
                        />
                      </th>
                      <th>Name</th>
                      <th>Record ID</th>
                      <th>Slug</th>
                      <th>Status</th>
                      <th className="text-right tabular-nums">Members</th>
                      <th className="text-right tabular-nums">Vaults</th>
                      <th>Created</th>
                      <th className="w-12" aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAdminOrgs.map((o, idx) => {
                      const recordLabel = o.record_id ?? o.id;
                      return (
                        <tr key={o.id}>
                          <td className="text-center text-xs tabular-nums text-[var(--color-text-subtle)]">
                            {idx + 1}
                          </td>
                          <td>
                            <input
                              type="checkbox"
                              checked={orgListSel.has(o.id)}
                              onChange={() => toggleOrgRow(o.id)}
                              aria-label={`Select ${o.name}`}
                              className="rounded border-[var(--color-border)]"
                            />
                          </td>
                          <td className="font-medium text-[var(--color-text)]">
                            <Link
                              to={`/app/organizations/${o.id}`}
                              className="text-[var(--color-accent)] hover:underline"
                            >
                              {o.name}
                            </Link>
                          </td>
                          <td className="font-mono text-xs">
                            <Link
                              to={`/app/organizations/${o.id}`}
                              className="font-medium text-[var(--color-accent)] hover:underline"
                            >
                              {recordLabel}
                            </Link>
                          </td>
                          <td className="font-mono text-xs text-[var(--color-text-muted)]">
                            {o.slug}
                          </td>
                          <td>
                            <span
                              className={cn(
                                "chip text-[11px]",
                                o.active ? "chip-blue" : "opacity-90",
                              )}
                              style={
                                o.active
                                  ? undefined
                                  : {
                                      borderColor: "rgba(255,149,0,0.35)",
                                      color: "#b45309",
                                    }
                              }
                            >
                              {o.active ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="text-right text-xs text-[var(--color-text-muted)] tabular-nums">
                            {o.member_count}
                          </td>
                          <td className="text-right text-xs text-[var(--color-text-muted)] tabular-nums">
                            {o.vault_count}
                          </td>
                          <td className="text-xs text-[var(--color-text-subtle)]">
                            {new Date(o.created_at).toLocaleDateString()}
                          </td>
                          <td className="text-right">
                            <SfRowMenu>
                              <Link
                                to={`/app/organizations/${o.id}`}
                                className="block rounded-md px-3 py-2 text-left text-xs text-[var(--color-text)] hover:bg-[var(--color-surface-2)]"
                              >
                                Open record
                              </Link>
                            </SfRowMenu>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </SfTableWrap>
            )}
          </SfListShell>

          <Card
            title="Role model"
            description="Self-signup assigns Public. Client admins invite staff; Dataly admins can assign any role."
          >
            <ul className="space-y-2 text-sm text-[var(--color-text-muted)]">
              <li className="flex gap-2">
                <UserCog className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-accent)]" />
                <span>
                  <strong className="text-[var(--color-text)]">Dataly admin</strong> — platform
                  operator. Directory, database, and Organization tab for tenants; tenant secret writes stay
                  with client admins.
                </span>
              </li>
              <li className="flex gap-2">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-accent)]" />
                <span>
                  <strong className="text-[var(--color-text)]">Client admin</strong> — manages secrets,
                  API keys, staff access, and adds client staff by email for their org.
                </span>
              </li>
              <li className="flex gap-2">
                <UsersIcon className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-accent)]" />
                <span>
                  <strong className="text-[var(--color-text)]">Client staff</strong> — granted by a
                  client admin. Uses per-store access controls for secrets.
                </span>
              </li>
              <li className="flex gap-2">
                <UserPlus className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-accent)]" />
                <span>
                  <strong className="text-[var(--color-text)]">Public</strong> — default after
                  self-registration; no secrets UI until promoted or invited as staff.
                </span>
              </li>
            </ul>
          </Card>
        </>
      )}
    </div>
  );
}

function MembersSection({
  members,
  canInviteStaff,
  vaults,
  inviteEmail,
  setInviteEmail,
  inviteVaultIds,
  setInviteVaultIds,
  inviteVaultPerm,
  setInviteVaultPerm,
  inviteOk,
  onInviteStaff,
  inviteSubmitting,
  detailMemberUserId,
  workspaceName,
}: {
  members: MemberRow[];
  canInviteStaff: boolean;
  vaults: Vault[];
  inviteEmail: string;
  setInviteEmail: (v: string) => void;
  inviteVaultIds: string[];
  setInviteVaultIds: (ids: string[] | ((prev: string[]) => string[])) => void;
  inviteVaultPerm: "read" | "read_write" | "admin";
  setInviteVaultPerm: (p: "read" | "read_write" | "admin") => void;
  inviteOk: string | null;
  onInviteStaff: (e: FormEvent) => void | Promise<void>;
  inviteSubmitting: boolean;
  detailMemberUserId: string | null;
  workspaceName: string;
}) {
  const navigate = useNavigate();
  const [tenantListSel, setTenantListSel] = useState<Set<string>>(new Set());
  const [tenantTab, setTenantTab] = useState<"details" | "related">("details");

  useEffect(() => {
    setTenantTab("details");
  }, [detailMemberUserId]);

  if (detailMemberUserId) {
    const m = members.find((x) => x.user_id === detailMemberUserId);
    if (!m) {
      return (
        <Card title="Member">
          <EmptyState>This person is not in the current workspace.</EmptyState>
          <button type="button" className="btn btn-secondary mt-4" onClick={() => navigate("/app")}>
            <ChevronLeftIcon className="h-4 w-4" /> Back to team
          </button>
        </Card>
      );
    }
    const rid = m.user_record_id ?? "—";
    const mid = m.membership_record_id ?? "—";
    return (
      <div className="flex flex-col gap-5">
        <SfRecordHero
          icon={<UsersIcon className="h-6 w-6" />}
          objectLabel="Workspace member"
          recordTitle={m.profiles?.display_name ?? m.user_id}
          subtitle={
            <span className="text-xs text-[var(--color-text-muted)]">
              {workspaceName} · {ORG_ROLE_LABEL[m.role] ?? m.role}
            </span>
          }
          highlights={[
            {
              label: "Role",
              value: (
                <span
                  className={cn(
                    "chip",
                    m.role === "client_admin" || m.role === "dataly_admin"
                      ? "chip-blue"
                      : m.role === "public_user"
                        ? "opacity-90"
                        : "",
                  )}
                >
                  {ORG_ROLE_LABEL[m.role] ?? m.role}
                </span>
              ),
            },
            {
              label: "User record",
              value: <span className="font-mono text-[11px]">{rid}</span>,
            },
            {
              label: "Membership record",
              value: <span className="font-mono text-[11px]">{mid}</span>,
            },
          ]}
          actions={
            <button type="button" className="btn btn-secondary" onClick={() => navigate("/app")}>
              <ChevronLeftIcon className="h-4 w-4" /> Back to team
            </button>
          }
        />
        <SfSplitDetail
          tabs={[
            { id: "details", label: "Details" },
            { id: "related", label: "Related" },
          ]}
          tabActive={tenantTab}
          onTabChange={(id) => setTenantTab(id as "details" | "related")}
          main={
            tenantTab === "details" ? (
              <SfFieldGrid
                rows={[
                  {
                    label: "Display name",
                    value: m.profiles?.display_name ?? "—",
                  },
                  { label: "Role", value: ORG_ROLE_LABEL[m.role] ?? m.role },
                  {
                    label: "User record ID",
                    value: <span className="font-mono text-xs">{rid}</span>,
                  },
                  {
                    label: "Membership record ID",
                    value: <span className="font-mono text-xs">{mid}</span>,
                  },
                  {
                    label: "Internal user ID",
                    value: (
                      <span className="break-all font-mono text-[11px]">{m.user_id}</span>
                    ),
                  },
                ]}
              />
            ) : (
              <p className="text-sm text-[var(--color-text-muted)]">
                Related lists (stores, keys, and fine-grained access) can surface here in a future update.
              </p>
            )
          }
          sidebar={
            <SfSidebarCard title="Workspace context" description={workspaceName}>
              <p className="text-xs text-[var(--color-text-muted)]">
                Tenant roles are managed in this workspace only. Platform-wide changes use{" "}
                <strong className="text-[var(--color-text)]">All users</strong> for Dataly admins.
              </p>
            </SfSidebarCard>
          }
        />
      </div>
    );
  }

  function toggleVault(id: string) {
    setInviteVaultIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  const tenantRowIds = members.map((m) => m.user_id);
  const allTenantRowsSelected =
    tenantRowIds.length > 0 && tenantRowIds.every((id) => tenantListSel.has(id));

  function toggleTenantRow(id: string) {
    setTenantListSel((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleTenantSelectAll() {
    setTenantListSel((prev) => {
      const next = new Set(prev);
      if (allTenantRowsSelected) tenantRowIds.forEach((id) => next.delete(id));
      else tenantRowIds.forEach((id) => next.add(id));
      return next;
    });
  }

  return (
    <div className="flex flex-col gap-5">
      {canInviteStaff && (
        <Card
          title="Add client staff"
          description="Creates a staff account if the email is new (temporary password emailed when SMTP is configured under Admin profile), or upgrades an existing member. Set From address and SMTP relay there."
        >
          <form onSubmit={onInviteStaff} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <div className="min-w-0 flex-1">
                <Field label="Email">
                <input
                  className="field-input"
                  type="text"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="colleague@company.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
                </Field>
              </div>
              <button
                type="submit"
                className="btn btn-primary shrink-0"
                disabled={inviteSubmitting || !inviteEmail.trim()}
              >
                <UserPlus className="h-4 w-4" /> Invite / create staff
              </button>
            </div>
            {vaults.length > 0 ? (
              <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] p-3">
                <p className="mb-2 text-xs font-medium text-[var(--color-text-muted)]">
                  Grant store access (optional)
                </p>
                <div className="flex flex-wrap gap-3">
                  {vaults.map((v) => (
                    <label
                      key={v.id}
                      className="flex cursor-pointer items-center gap-2 text-sm text-[var(--color-text)]"
                    >
                      <input
                        type="checkbox"
                        checked={inviteVaultIds.includes(v.id)}
                        onChange={() => toggleVault(v.id)}
                        className="rounded border-[var(--color-border)]"
                      />
                      <span>{v.name}</span>
                    </label>
                  ))}
                </div>
                <div className="mt-3 max-w-xs">
                  <Field label="Permission for selected stores">
                    <select
                      className="field-input"
                      value={inviteVaultPerm}
                      onChange={(e) =>
                        setInviteVaultPerm(e.target.value as "read" | "read_write" | "admin")
                      }
                    >
                      <option value="read">Read</option>
                      <option value="read_write">Read & write</option>
                      <option value="admin">Admin</option>
                    </select>
                  </Field>
                </div>
              </div>
            ) : null}
            {inviteOk ? (
              <p
                className="text-sm"
                style={{ color: "#1f8b3a" }}
              >
                {inviteOk}
              </p>
            ) : null}
          </form>
        </Card>
      )}

      <SfListShell
        objectLabel="Workspace team"
        viewLabel={workspaceName}
        metaRight={`${members.length} member${members.length === 1 ? "" : "s"}`}
        actions={
          canInviteStaff ? (
            <span className="text-[11px] text-[var(--color-text-muted)]">Use Add client staff above</span>
          ) : null
        }
      >
        {members.length === 0 ? (
          <div className="p-6">
            <EmptyState>No members yet.</EmptyState>
          </div>
        ) : (
          <SfTableWrap>
            <table className="dl-table">
              <thead>
                <tr>
                  <th className="w-10 text-center text-[var(--color-text-subtle)]">#</th>
                  <th className="w-10">
                    <input
                      type="checkbox"
                      checked={allTenantRowsSelected}
                      onChange={() => toggleTenantSelectAll()}
                      aria-label="Select all members"
                      className="rounded border-[var(--color-border)]"
                    />
                  </th>
                  <th>Name</th>
                  <th>Record ID</th>
                  <th>Role</th>
                  <th className="w-12" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {members.map((m, idx) => {
                  const recordLabel = m.user_record_id ?? m.user_id;
                  return (
                    <tr key={m.user_id}>
                      <td className="text-center text-xs tabular-nums text-[var(--color-text-subtle)]">
                        {idx + 1}
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={tenantListSel.has(m.user_id)}
                          onChange={() => toggleTenantRow(m.user_id)}
                          aria-label={`Select ${m.profiles?.display_name ?? m.user_id}`}
                          className="rounded border-[var(--color-border)]"
                        />
                      </td>
                      <td className="font-medium text-[var(--color-text)]">
                        <Link
                          to={`/app/members/${m.user_id}`}
                          className="text-[var(--color-accent)] hover:underline"
                        >
                          {m.profiles?.display_name ?? m.user_id}
                        </Link>
                      </td>
                      <td className="font-mono text-[11px]">
                        <Link
                          to={`/app/members/${m.user_id}`}
                          className="text-[var(--color-accent)] hover:underline"
                        >
                          {recordLabel}
                        </Link>
                      </td>
                      <td>
                        <span
                          className={cn(
                            "chip text-[11px]",
                            m.role === "client_admin" || m.role === "dataly_admin"
                              ? "chip-blue"
                              : m.role === "public_user"
                                ? "opacity-90"
                                : "",
                          )}
                        >
                          {ORG_ROLE_LABEL[m.role] ?? m.role}
                        </span>
                      </td>
                      <td className="text-right">
                        <SfRowMenu>
                          <Link
                            to={`/app/members/${m.user_id}`}
                            className="block rounded-md px-3 py-2 text-left text-xs text-[var(--color-text)] hover:bg-[var(--color-surface-2)]"
                          >
                            Open record
                          </Link>
                        </SfRowMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </SfTableWrap>
        )}
      </SfListShell>
    </div>
  );
}

function ApiKeysSection({
  edgeBase,
  apiKeys,
  apiKeyName,
  setApiKeyName,
  revealedKey,
  handleCreateApiKey,
  revokeKey,
}: {
  edgeBase: string;
  apiKeys: ApiKeyRow[];
  apiKeyName: string;
  setApiKeyName: (v: string) => void;
  revealedKey: string | null;
  handleCreateApiKey: (e: FormEvent) => Promise<void>;
  revokeKey: (id: string) => Promise<void>;
}) {
  return (
    <div className="flex flex-col gap-5">
      <Card
        title="Create key"
        description={
          <span>
            Use as{" "}
            <code className="rounded bg-[var(--color-surface-3)] px-1 py-0.5 font-mono text-[11px]">
              Authorization: Bearer …
            </code>{" "}
            on the HTTP API.
          </span>
        }
      >
        <form onSubmit={handleCreateApiKey} className="flex flex-col gap-2 sm:flex-row">
          <input
            className="field-input flex-1"
            placeholder="Label (e.g. GitHub Actions)"
            value={apiKeyName}
            onChange={(e) => setApiKeyName(e.target.value)}
          />
          <button type="submit" className="btn btn-primary">
            <Plus className="h-4 w-4" /> Generate key
          </button>
        </form>
        {revealedKey && (
          <div
            className="mt-3 rounded-lg border px-3 py-2.5 text-sm"
            style={{
              borderColor: "rgba(255,204,0,0.4)",
              background: "rgba(255,204,0,0.08)",
              color: "#8a6500",
            }}
          >
            <p className="text-[11px] font-semibold uppercase tracking-wide">
              Copy now — this is the only time we show the secret
            </p>
            <div className="mt-1.5 flex items-center gap-2">
              <code className="flex-1 break-all font-mono text-[12px] text-[var(--color-text)]">
                {revealedKey}
              </code>
              <CopyButton value={revealedKey} />
            </div>
          </div>
        )}
        <pre className="mt-4 overflow-x-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] p-3 font-mono text-[11px] text-[var(--color-text-muted)]">
{`curl "${edgeBase}?resource=secrets" \\
  -H "Authorization: Bearer YOUR_MSM_KEY"`}
        </pre>
      </Card>

      <Card title={`Keys (${apiKeys.length})`}>
        {apiKeys.length === 0 ? (
          <EmptyState>No API keys yet.</EmptyState>
        ) : (
          <ul className="divide-y divide-[var(--color-border)]">
            {apiKeys.map((k) => (
              <li key={k.id} className="flex flex-wrap items-center justify-between gap-2 py-3">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[var(--color-surface-3)] text-[var(--color-accent)]">
                    <Code2 className="h-[18px] w-[18px]" />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-[var(--color-text)]">
                      {k.name}
                    </p>
                    <p className="truncate font-mono text-[11px] text-[var(--color-text-subtle)]">
                      {k.key_prefix}… · {k.scopes.join(", ")}
                    </p>
                  </div>
                </div>
                {k.revoked_at ? (
                  <span className="chip chip-danger">Revoked</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => revokeKey(k.id)}
                    className="btn btn-danger"
                  >
                    <Trash2 className="h-4 w-4" /> Revoke
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function AuditSection({
  orgId,
  auditQuery,
  auditLogs,
  auditTotal,
  auditDraft,
  setAuditDraft,
  setAuditQuery,
  fetchAudit,
  showToast,
}: {
  orgId: string;
  auditQuery: string;
  auditLogs: AuditLogRow[];
  auditTotal: number;
  auditDraft: string;
  setAuditDraft: (v: string) => void;
  setAuditQuery: (v: string) => void;
  fetchAudit: () => Promise<void>;
  showToast: (message: string, kind?: "ok" | "err") => void;
}) {
  async function exportAudit(format: "csv" | "ndjson") {
    try {
      await downloadAuditExport(orgId, format, {
        action: auditQuery.trim() || undefined,
      });
      showToast(`Audit export (${format}) started — check your downloads.`, "ok");
    } catch (e: unknown) {
      showToast(friendlyAuthError(e), "err");
    }
  }

  return (
    <Card
      title="Events"
      description={`Showing ${auditLogs.length} of ${auditTotal}. Export uses the same action filter when applied.`}
      action={
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Filter className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--color-text-subtle)]" />
            <input
              className="control-input pl-7 min-w-[200px]"
              placeholder="Filter by action (e.g. secret.create)"
              value={auditDraft}
              onChange={(e) => setAuditDraft(e.target.value)}
            />
          </div>
          <button
            type="button"
            onClick={() => setAuditQuery(auditDraft.trim())}
            className="btn btn-secondary"
          >
            Apply
          </button>
          <button
            type="button"
            onClick={() => void fetchAudit()}
            className="btn btn-ghost"
          >
            <RefreshCcw className="h-3.5 w-3.5" /> Refresh
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => void exportAudit("csv")}
          >
            <Download className="h-3.5 w-3.5" /> CSV
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => void exportAudit("ndjson")}
          >
            <Download className="h-3.5 w-3.5" /> NDJSON
          </button>
        </div>
      }
    >
      {auditLogs.length === 0 ? (
        <EmptyState>No audit entries match.</EmptyState>
      ) : (
        <div className="overflow-x-auto">
          <table className="dl-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Audit record</th>
                <th>Action</th>
                <th>Actor</th>
                <th>Resource</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {auditLogs.map((a) => (
                <tr key={a.id}>
                  <td className="whitespace-nowrap text-xs text-[var(--color-text-subtle)]">
                    {new Date(a.created_at).toLocaleString()}
                  </td>
                  <td className="max-w-[140px] truncate font-mono text-[10px] text-[var(--color-text-muted)]">
                    {a.record_id ?? "—"}
                  </td>
                  <td className="font-mono text-[11px] text-[var(--color-text)]">
                    {a.action}
                  </td>
                  <td className="text-[var(--color-text-muted)]">
                    {a.actor_type}
                    {a.actor_email && (
                      <span className="ml-1 text-[var(--color-text-subtle)]">
                        {a.actor_email}
                      </span>
                    )}
                    {!a.actor_email && a.actor_api_key_id && (
                      <span className="ml-1 font-mono text-[var(--color-text-subtle)]">
                        {String(a.actor_api_key_id).slice(0, 8)}…
                      </span>
                    )}
                  </td>
                  <td className="font-mono text-[11px] text-[var(--color-text-subtle)]">
                    {a.resource_type ?? "—"}
                    {a.resource_id && (
                      <span className="block max-w-[120px] truncate">{a.resource_id}</span>
                    )}
                  </td>
                  <td className="max-w-xs truncate font-mono text-[10px] text-[var(--color-text-subtle)]">
                    {JSON.stringify(a.metadata)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/** Inline help for Admin profile → Outbound mail (workspace + platform SMTP). */
function AdminMailSmtpGuide() {
  return (
    <div className="space-y-4 text-xs leading-relaxed text-[var(--color-text-muted)]">
      <div>
        <p className="font-semibold text-[var(--color-text)]">How mail leaves the app</p>
        <ul className="mt-1.5 list-disc space-y-1 pl-4">
          <li>
            Staff invitations use your <strong className="font-medium text-[var(--color-text)]">From email</strong>{" "}
            (or your login email if empty) as the visible sender / Reply-To.
          </li>
          <li>
            The API picks SMTP in this order:{" "}
            <strong className="font-medium text-[var(--color-text)]">workspace relay</strong> (below), then{" "}
            <strong className="font-medium text-[var(--color-text)]">platform relay</strong> (Dataly admins only),
            then server <code className="rounded bg-[var(--color-surface-3)] px-1 font-mono text-[11px]">SMTP_*</code>{" "}
            environment variables.
          </li>
        </ul>
      </div>
      <div>
        <p className="font-semibold text-[var(--color-text)]">What to enter</p>
        <dl className="mt-1.5 space-y-2">
          <div>
            <dt className="font-medium text-[var(--color-text)]">SMTP host</dt>
            <dd className="mt-0.5 pl-0">
              Your provider’s outbound hostname (for example SendGrid{" "}
              <code className="font-mono text-[11px]">smtp.sendgrid.net</code>, Google Workspace{" "}
              <code className="font-mono text-[11px]">smtp.gmail.com</code>, Amazon SES SMTP endpoint for your
              region).
            </dd>
          </div>
          <div>
            <dt className="font-medium text-[var(--color-text)]">Port &amp; TLS</dt>
            <dd className="mt-0.5">
              <strong className="text-[var(--color-text)]">587</strong> with{" "}
              <strong className="text-[var(--color-text)]">Use TLS (SSL) unchecked</strong> is the usual STARTTLS
              setup. Use <strong className="text-[var(--color-text)]">465</strong> with{" "}
              <strong className="text-[var(--color-text)]">Use TLS checked</strong> when your provider requires SSL
              on connection (often labeled SMTPS).
            </dd>
          </div>
          <div>
            <dt className="font-medium text-[var(--color-text)]">Username / password</dt>
            <dd className="mt-0.5">
              Provider login or API user (SendGrid often uses username{" "}
              <code className="font-mono text-[11px]">apikey</code> and the API key as password). Leave blank only if
              your relay allows unauthenticated sending on your network (uncommon on the public internet).
            </dd>
          </div>
          <div>
            <dt className="font-medium text-[var(--color-text)]">Envelope From (optional)</dt>
            <dd className="mt-0.5">
              Overrides the SMTP envelope sender if your provider requires a verified domain. Must match what your
              provider allows; DKIM/SPF at your DNS still apply.
            </dd>
          </div>
        </dl>
      </div>
      <div>
        <p className="font-semibold text-[var(--color-text)]">After you save</p>
        <p className="mt-1">
          Send a test by inviting client staff from Organization, or ask a Dataly admin to confirm platform SMTP if
          this workspace has no host set. Credentials are stored on the API database — restrict database access
          accordingly.
        </p>
      </div>
    </div>
  );
}

function WorkspaceSmtpCard({ organizationId }: { organizationId: string }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [host, setHost] = useState("");
  const [port, setPort] = useState("587");
  const [secure, setSecure] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [hasPass, setHasPass] = useState(false);
  const [defaultFrom, setDefaultFrom] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const d = await fetchOrgSmtpSettings(organizationId);
        if (cancelled) return;
        const s = d.settings;
        setHost(s.host);
        setPort(String(s.port));
        setSecure(s.secure);
        setUsername(s.username);
        setHasPass(s.has_password);
        setDefaultFrom(s.default_from);
        setPassword("");
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : "Failed to load SMTP");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  async function saveSmtp(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    setOk(null);
    try {
      const patch: SmtpSettingsPatch = {
        host: host.trim(),
        port: Math.min(Math.max(Number(port) || 587, 1), 65535),
        secure,
        username: username.trim(),
        default_from: defaultFrom.trim(),
      };
      if (password.trim()) patch.password = password.trim();
      const out = await patchOrgSmtpSettings(organizationId, patch);
      setHasPass(out.settings.has_password);
      setPassword("");
      setOk("Workspace SMTP saved.");
    } catch (e) {
      setErr(friendlyAuthError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card
      title="Workspace SMTP relay"
      description="Used first for this workspace’s invitations. Leave SMTP host empty to use platform SMTP or server env vars."
    >
      {loading ? (
        <EmptyState>Loading SMTP settings…</EmptyState>
      ) : (
        <form onSubmit={saveSmtp} className="flex flex-col gap-3">
          {err && (
            <p className="rounded-lg border border-[rgba(255,59,48,0.25)] bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger)]">
              {err}
            </p>
          )}
          {ok && (
            <p className="text-sm" style={{ color: "#1f8b3a" }}>
              {ok}
            </p>
          )}
          <Field label="SMTP host" hint="e.g. smtp.sendgrid.net">
            <input
              className="field-input font-mono text-sm"
              value={host}
              onChange={(e) => setHost(e.target.value)}
              disabled={saving}
              placeholder="smtp.example.com"
              autoComplete="off"
            />
          </Field>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Port">
              <input
                className="field-input font-mono text-sm"
                type="number"
                min={1}
                max={65535}
                value={port}
                onChange={(e) => setPort(e.target.value)}
                disabled={saving}
              />
            </Field>
            <Field label="TLS">
              <label className="flex cursor-pointer items-center gap-2 text-sm text-[var(--color-text)]">
                <input
                  type="checkbox"
                  checked={secure}
                  onChange={(e) => setSecure(e.target.checked)}
                  disabled={saving}
                />
                Use TLS (SSL)
              </label>
            </Field>
          </div>
          <Field label="Username" hint="Optional — leave blank if not required">
            <input
              className="field-input font-mono text-sm"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={saving}
              autoComplete="off"
            />
          </Field>
          <Field
            label="Password"
            hint={
              hasPass
                ? "Leave blank to keep the saved password."
                : "Optional if your relay allows unauthenticated sending."
            }
          >
            <input
              className="field-input font-mono text-sm"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={saving}
              autoComplete="new-password"
              placeholder={hasPass ? "••••••••" : ""}
            />
          </Field>
          <Field label="Envelope From (optional)" hint="Overrides SMTP_FROM / envelope when set">
            <input
              className="field-input font-mono text-sm"
              type="text"
              inputMode="email"
              value={defaultFrom}
              onChange={(e) => setDefaultFrom(e.target.value)}
              disabled={saving}
              placeholder="noreply@yourdomain.com"
            />
          </Field>
          <button type="submit" className="btn btn-primary w-fit" disabled={saving}>
            Save workspace SMTP
          </button>
        </form>
      )}
    </Card>
  );
}

function PlatformSmtpCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [host, setHost] = useState("");
  const [port, setPort] = useState("587");
  const [secure, setSecure] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [hasPass, setHasPass] = useState(false);
  const [defaultFrom, setDefaultFrom] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const d = await fetchPlatformSmtpSettings();
        if (cancelled) return;
        const s = d.settings;
        setHost(s.host);
        setPort(String(s.port));
        setSecure(s.secure);
        setUsername(s.username);
        setHasPass(s.has_password);
        setDefaultFrom(s.default_from);
        setPassword("");
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : "Failed to load SMTP");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function saveSmtp(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    setOk(null);
    try {
      const patch: SmtpSettingsPatch = {
        host: host.trim(),
        port: Math.min(Math.max(Number(port) || 587, 1), 65535),
        secure,
        username: username.trim(),
        default_from: defaultFrom.trim(),
      };
      if (password.trim()) patch.password = password.trim();
      const out = await patchPlatformSmtpSettings(patch);
      setHasPass(out.settings.has_password);
      setPassword("");
      setOk("Platform SMTP saved.");
    } catch (e) {
      setErr(friendlyAuthError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card
      title="Platform SMTP relay (fallback)"
      description="Used when a workspace has no SMTP host configured. Applies to every tenant unless overridden."
    >
      {loading ? (
        <EmptyState>Loading SMTP settings…</EmptyState>
      ) : (
        <form onSubmit={saveSmtp} className="flex flex-col gap-3">
          {err && (
            <p className="rounded-lg border border-[rgba(255,59,48,0.25)] bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger)]">
              {err}
            </p>
          )}
          {ok && (
            <p className="text-sm" style={{ color: "#1f8b3a" }}>
              {ok}
            </p>
          )}
          <Field label="SMTP host">
            <input
              className="field-input font-mono text-sm"
              value={host}
              onChange={(e) => setHost(e.target.value)}
              disabled={saving}
              placeholder="smtp.example.com"
              autoComplete="off"
            />
          </Field>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Port">
              <input
                className="field-input font-mono text-sm"
                type="number"
                min={1}
                max={65535}
                value={port}
                onChange={(e) => setPort(e.target.value)}
                disabled={saving}
              />
            </Field>
            <Field label="TLS">
              <label className="flex cursor-pointer items-center gap-2 text-sm text-[var(--color-text)]">
                <input
                  type="checkbox"
                  checked={secure}
                  onChange={(e) => setSecure(e.target.checked)}
                  disabled={saving}
                />
                Use TLS (SSL)
              </label>
            </Field>
          </div>
          <Field label="Username">
            <input
              className="field-input font-mono text-sm"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={saving}
              autoComplete="off"
            />
          </Field>
          <Field
            label="Password"
            hint={hasPass ? "Leave blank to keep the saved password." : undefined}
          >
            <input
              className="field-input font-mono text-sm"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={saving}
              autoComplete="new-password"
              placeholder={hasPass ? "••••••••" : ""}
            />
          </Field>
          <Field label="Envelope From (optional)">
            <input
              className="field-input font-mono text-sm"
              type="text"
              inputMode="email"
              value={defaultFrom}
              onChange={(e) => setDefaultFrom(e.target.value)}
              disabled={saving}
            />
          </Field>
          <button type="submit" className="btn btn-primary w-fit" disabled={saving}>
            Save platform SMTP
          </button>
        </form>
      )}
    </Card>
  );
}

function AdminProfileSection({
  loginEmail,
  organizationId,
  showWorkspaceSmtp,
  showPlatformSmtp,
}: {
  loginEmail: string;
  organizationId: string | null;
  showWorkspaceSmtp: boolean;
  showPlatformSmtp: boolean;
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [phone, setPhone] = useState("");
  const [senderEmail, setSenderEmail] = useState("");
  const [recordId, setRecordId] = useState<string | null>(null);
  const [profileTab, setProfileTab] = useState<"mail" | "account">("mail");
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const d = await fetchMyProfile();
        if (cancelled) return;
        setDisplayName(d.profile.display_name ?? "");
        setPhone(d.profile.phone ?? "");
        setSenderEmail(d.profile.sender_email ?? "");
        setRecordId(d.user.record_id ?? null);
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : "Failed to load profile");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function onSave(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    setOk(null);
    try {
      await patchMyProfile({
        display_name: displayName.trim() || null,
        phone: phone.trim() || null,
        sender_email: senderEmail.trim() ? senderEmail.trim().toLowerCase() : null,
      });
      setOk("Saved.");
    } catch (e) {
      setErr(friendlyAuthError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-5">
      {loading ? (
        <Card title="Admin profile">
          <EmptyState>Loading profile…</EmptyState>
        </Card>
      ) : (
        <>
          <SfRecordHero
            icon={<UserCog className="h-6 w-6" />}
            objectLabel="Admin profile"
            recordTitle={displayName.trim() || loginEmail}
            subtitle={
              <span className="font-mono text-[11px] text-[var(--color-text-subtle)]">{loginEmail}</span>
            }
            highlights={[
              {
                label: "Record ID",
                value: (
                  <span className="font-mono text-[11px]">{recordId ?? "—"}</span>
                ),
              },
              {
                label: "Invitation From",
                value: senderEmail.trim() ? senderEmail : loginEmail,
              },
            ]}
            actions={
              profileTab === "mail" ? (
                <button type="submit" form="admin-profile-mail-form" className="btn btn-primary" disabled={saving}>
                  Save changes
                </button>
              ) : null
            }
          />

          <SfSplitDetail
            tabs={[
              { id: "mail", label: "Outbound mail" },
              { id: "account", label: "Account" },
            ]}
            tabActive={profileTab}
            onTabChange={(id) => setProfileTab(id as "mail" | "account")}
            main={
              profileTab === "mail" ? (
                <div className="flex flex-col gap-5">
                  <div className="flex flex-col gap-3">
                    <p className="text-xs text-[var(--color-text-muted)]">
                      Messages to invited staff use the From address below (falling back to your login email).
                      Configure SMTP below so welcome emails send; Reply-To still uses this From address.
                    </p>
                    <form id="admin-profile-mail-form" onSubmit={onSave} className="flex flex-col gap-3">
                      {err && (
                        <p className="rounded-lg border border-[rgba(255,59,48,0.25)] bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger)]">
                          {err}
                        </p>
                      )}
                      {ok && (
                        <p className="text-sm" style={{ color: "#1f8b3a" }}>
                          {ok}
                        </p>
                      )}
                      <Field label="Display name" hint="Shown to teammates">
                        <input
                          className="field-input"
                          value={displayName}
                          onChange={(e) => setDisplayName(e.target.value)}
                          disabled={saving}
                          placeholder="Your name"
                        />
                      </Field>
                      <Field label="Phone" hint="Optional">
                        <input
                          className="field-input"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          disabled={saving}
                          placeholder="+1 …"
                        />
                      </Field>
                      <Field label="From email (invitations)" hint="Optional — defaults to login email">
                        <input
                          className="field-input"
                          type="text"
                          inputMode="email"
                          autoComplete="email"
                          value={senderEmail}
                          onChange={(e) => setSenderEmail(e.target.value)}
                          disabled={saving}
                          placeholder={loginEmail}
                        />
                      </Field>
                    </form>
                  </div>
                  {(showWorkspaceSmtp && organizationId) || showPlatformSmtp ? (
                    <details className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-2)] px-4 py-3">
                      <summary className="cursor-pointer text-sm font-semibold text-[var(--color-text)]">
                        SMTP setup guide — what to enter here
                      </summary>
                      <div className="mt-3 max-h-[min(70vh,520px)] overflow-y-auto border-t border-[var(--color-border)] pt-3">
                        <AdminMailSmtpGuide />
                      </div>
                    </details>
                  ) : null}
                  {showWorkspaceSmtp && organizationId ? (
                    <WorkspaceSmtpCard organizationId={organizationId} />
                  ) : null}
                  {showPlatformSmtp ? <PlatformSmtpCard /> : null}
                </div>
              ) : (
                <SfFieldGrid
                  rows={[
                    {
                      label: "Login email",
                      value: <span className="font-mono text-xs">{loginEmail}</span>,
                    },
                    {
                      label: "Record ID",
                      value: (
                        <span className="font-mono text-xs">{recordId ?? "—"}</span>
                      ),
                    },
                    {
                      label: "Note",
                      value:
                        "Rename or deactivate this account from All users when you have platform admin access.",
                    },
                  ]}
                />
              )
            }
            sidebar={
              profileTab === "mail" ? (
                <SfSidebarCard title="SMTP setup guide" description="Relay, ports, and sender fields">
                  <div className="max-h-[min(75vh,560px)] overflow-y-auto pr-1">
                    <AdminMailSmtpGuide />
                  </div>
                </SfSidebarCard>
              ) : (
                <SfSidebarCard title="Account" description="Profile record">
                  <p className="text-xs text-[var(--color-text-muted)]">
                    Login email and record ID are stable identifiers across workspaces and audit logs.
                  </p>
                </SfSidebarCard>
              )
            }
          />
        </>
      )}
    </div>
  );
}

function SettingsSection({
  edgeBase,
  orgName,
  orgSlug,
  email,
  roleLabel,
}: {
  edgeBase: string;
  orgName: string;
  orgSlug: string;
  email: string;
  roleLabel: string;
}) {
  return (
    <div className="flex flex-col gap-5">
      <Card title="Workspace" description="Read-only for now — editable settings land in a follow-up.">
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
          <DetailRow icon={Building2} label="Name" value={orgName} />
          <DetailRow icon={Building2} label="Slug" value={orgSlug} mono />
        </dl>
      </Card>

      <Card title="Account">
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
          <DetailRow icon={Mail} label="Email" value={email} />
          <DetailRow icon={ShieldCheck} label="Role" value={roleLabel} />
        </dl>
      </Card>

      <Card title="Integration endpoint" description="Use this base URL with API keys.">
        <pre className="overflow-x-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] p-3 font-mono text-xs text-[var(--color-text-muted)]">
{edgeBase}
        </pre>
      </Card>
    </div>
  );
}

function DetailRow({
  icon: Icon,
  label,
  value,
  mono,
}: {
  icon: typeof Mail;
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] p-3">
      <Icon className="mt-0.5 h-4 w-4 text-[var(--color-accent)]" />
      <div className="min-w-0">
        <dt className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--color-text-subtle)]">
          {label}
        </dt>
        <dd className={cn("mt-0.5 text-sm text-[var(--color-text)]", mono && "font-mono")}>
          {value}
        </dd>
      </div>
    </div>
  );
}

/* ─────────── Platform user directory (dataly_admin only) ─────────── */

function PlatformUsersSection({
  currentUserId,
  detailUserId,
  onCreate,
}: {
  currentUserId: string;
  detailUserId: string | null;
  onCreate?: () => void;
}) {
  const navigate = useNavigate();
  const [users, setUsers] = useState<AdminDirectoryUser[]>([]);
  const [total, setTotal] = useState(0);
  const pageSize = 50;
  const [page, setPage] = useState(0);
  const [qDraft, setQDraft] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [dirListSel, setDirListSel] = useState<Set<string>>(new Set());
  const [dirRecTab, setDirRecTab] = useState<"details" | "memberships">("details");
  const [detailBundle, setDetailBundle] = useState<{
    user: AdminDirectoryUser;
    memberships: AdminDirectoryMembership[];
  } | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [editUser, setEditUser] = useState<AdminDirectoryUser | null>(null);
  const [editEmail, setEditEmail] = useState("");
  const [editName, setEditName] = useState("");
  const [editPhone, setEditPhone] = useState("");

  const [pwUser, setPwUser] = useState<AdminDirectoryUser | null>(null);
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");

  const offset = page * pageSize;

  const load = useCallback(async () => {
    setBusy(true);
    setErr(null);
    try {
      const d = await adminDirectoryUsers({
        limit: pageSize,
        offset,
        q: q || undefined,
      });
      setUsers(d.users);
      setTotal(d.total);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load users");
    } finally {
      setBusy(false);
    }
  }, [offset, q]);

  useEffect(() => {
    void load();
  }, [load]);

  const refreshDetail = useCallback(async () => {
    if (!detailUserId) return;
    const d = await adminDirectoryUserDetail(detailUserId);
    setDetailBundle(d);
  }, [detailUserId]);

  useEffect(() => {
    if (!detailUserId) {
      setDetailBundle(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    (async () => {
      try {
        const d = await adminDirectoryUserDetail(detailUserId);
        if (!cancelled) setDetailBundle(d);
      } catch {
        if (!cancelled) setDetailBundle(null);
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [detailUserId]);

  useEffect(() => {
    setDirRecTab("details");
  }, [detailUserId]);

  function openEdit(u: AdminDirectoryUser) {
    setEditUser(u);
    setEditEmail(u.email);
    setEditName(u.display_name ?? "");
    setEditPhone(u.phone ?? "");
  }

  function openPw(u: AdminDirectoryUser) {
    setPwUser(u);
    setPw1("");
    setPw2("");
  }

  async function submitEdit(e: FormEvent) {
    e.preventDefault();
    if (!editUser) return;
    const editTargetId = editUser.id;
    setBusy(true);
    setErr(null);
    try {
      await adminPatchDirectoryUser(editTargetId, {
        email: editEmail.trim().toLowerCase(),
        display_name: editName.trim(),
        phone: editPhone.trim(),
      });
      setEditUser(null);
      await load();
      if (detailUserId === editTargetId) {
        try {
          await refreshDetail();
        } catch {
          setDetailBundle(null);
        }
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Update failed");
    } finally {
      setBusy(false);
    }
  }

  async function submitPw(e: FormEvent) {
    e.preventDefault();
    if (!pwUser || pw1.length < 6 || pw1 !== pw2) return;
    const pwTargetId = pwUser.id;
    setBusy(true);
    setErr(null);
    try {
      await adminResetDirectoryUserPassword(pwTargetId, pw1);
      setPwUser(null);
      setPw1("");
      setPw2("");
      await load();
      if (detailUserId === pwTargetId) await refreshDetail();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Password reset failed");
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(u: AdminDirectoryUser) {
    const verb = u.active ? "Deactivate" : "Reactivate";
    if (!window.confirm(`${verb} ${u.email}?`)) return;
    setBusy(true);
    setErr(null);
    try {
      if (u.active) await adminDeactivateDirectoryUser(u.id);
      else await adminActivateDirectoryUser(u.id);
      await load();
      if (detailUserId === u.id) await refreshDetail();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  async function removeUser(u: AdminDirectoryUser) {
    if (
      !window.confirm(
        `Permanently DELETE ${u.email} and all memberships / owned rows that cascade? This cannot be undone.`,
      )
    ) {
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await adminDeleteDirectoryUser(u.id);
      await load();
      if (detailUserId === u.id) navigate("/app", { replace: true });
    } catch (e) {
      setErr(friendlyAuthError(e));
    } finally {
      setBusy(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const filteredDirIds = users.map((u) => u.id);
  const allDirRowsSelected =
    filteredDirIds.length > 0 && filteredDirIds.every((id) => dirListSel.has(id));

  function toggleDirRow(id: string) {
    setDirListSel((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleDirSelectAll() {
    setDirListSel((prev) => {
      const next = new Set(prev);
      if (allDirRowsSelected) filteredDirIds.forEach((id) => next.delete(id));
      else filteredDirIds.forEach((id) => next.add(id));
      return next;
    });
  }

  if (detailUserId) {
    if (detailLoading && !detailBundle) {
      return (
        <Card title="User">
          <EmptyState>Loading account…</EmptyState>
        </Card>
      );
    }
    if (!detailBundle) {
      return (
        <Card title="User">
          <EmptyState>No user matches this link.</EmptyState>
          <button type="button" className="btn btn-secondary mt-4" onClick={() => navigate("/app")}>
            <ChevronLeftIcon className="h-4 w-4" /> Back to directory
          </button>
        </Card>
      );
    }

    const u = detailBundle.user;
    const isSelf = u.id === currentUserId;

    return (
      <div className="flex flex-col gap-5">
        {err && (
          <div className="rounded-lg border border-[rgba(255,59,48,0.25)] bg-[var(--color-danger-bg)] px-4 py-3 text-sm text-[var(--color-danger)]">
            {err}
          </div>
        )}

        <SfRecordHero
          icon={<UserRoundSearch className="h-6 w-6" />}
          objectLabel="User"
          recordTitle={u.display_name?.trim() || u.email}
          subtitle={<span className="font-mono text-[11px] text-[var(--color-text-subtle)]">{u.email}</span>}
          highlights={[
            {
              label: "Status",
              value: u.active ? (
                <span className="chip chip-blue">Active</span>
              ) : (
                <span className="chip chip-danger">Disabled</span>
              ),
            },
            { label: "Workspaces", value: u.org_count },
            {
              label: "Joined",
              value: new Date(u.created_at).toLocaleDateString(),
            },
            {
              label: "User ID",
              value: <span className="break-all font-mono text-[11px]">{u.id}</span>,
            },
          ]}
          actions={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => navigate("/app")}>
                <ChevronLeftIcon className="h-4 w-4" /> Back to list
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => void load()} disabled={busy}>
                <RefreshCcw className={cn("h-4 w-4", busy && "spin")} /> Refresh
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => openEdit(u)} disabled={busy}>
                <PencilLine className="h-4 w-4" /> Edit
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => openPw(u)} disabled={busy}>
                <KeyRound className="h-4 w-4" /> Reset password
              </button>
              {!isSelf && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => void toggleActive(u)}
                  disabled={busy}
                >
                  {u.active ? <Ban className="h-4 w-4" /> : <CircleCheck className="h-4 w-4" />}
                  {u.active ? " Deactivate" : " Activate"}
                </button>
              )}
              {!isSelf && (
                <button
                  type="button"
                  className="btn btn-secondary text-[var(--color-danger)]"
                  onClick={() => void removeUser(u)}
                  disabled={busy}
                >
                  <Trash2 className="h-4 w-4" /> Delete
                </button>
              )}
            </>
          }
        />

        <SfSplitDetail
          tabs={[
            { id: "details", label: "Details" },
            { id: "memberships", label: "Workspaces" },
          ]}
          tabActive={dirRecTab}
          onTabChange={(id) => setDirRecTab(id as "details" | "memberships")}
          main={
            dirRecTab === "details" ? (
              <SfFieldGrid
                rows={[
                  { label: "Email", value: <span className="font-mono text-xs">{u.email}</span> },
                  { label: "Full name", value: u.display_name ?? "—" },
                  { label: "Phone", value: u.phone ?? "—" },
                  {
                    label: "Status",
                    value: u.active ? "Active" : "Disabled",
                  },
                  { label: "Workspace memberships", value: u.org_count },
                  {
                    label: "Created",
                    value: new Date(u.created_at).toLocaleString(),
                  },
                  {
                    label: "User ID",
                    value: <span className="break-all font-mono text-[11px]">{u.id}</span>,
                  },
                ]}
              />
            ) : (
              <SfTableWrap>
                {detailBundle.memberships.length === 0 ? (
                  <EmptyState>No workspace memberships.</EmptyState>
                ) : (
                  <table className="dl-table">
                    <thead>
                      <tr>
                        <th>Workspace</th>
                        <th>Slug</th>
                        <th>Role</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailBundle.memberships.map((m) => (
                        <tr key={`${m.organization_id}-${m.role}`}>
                          <td className="text-sm text-[var(--color-text)]">{m.org_name}</td>
                          <td className="font-mono text-xs text-[var(--color-text-muted)]">{m.org_slug}</td>
                          <td className="text-xs">{ORG_ROLE_LABEL[m.role] ?? m.role}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </SfTableWrap>
            )
          }
          sidebar={
            <SfSidebarCard title="Record tips" description="Profile edits apply immediately across tenants.">
              <p className="text-xs text-[var(--color-text-muted)]">
                Delete removes the account and cascading memberships when allowed by the server.
              </p>
            </SfSidebarCard>
          }
        />

        {editUser && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
          >
            <div
              className="w-full max-w-md rounded-2xl bg-[var(--color-surface)] p-6"
              style={{ boxShadow: "var(--shadow-modal)" }}
            >
              <h2 className="text-lg font-semibold text-[var(--color-text)]">Edit user</h2>
              <p className="mt-1 font-mono text-xs text-[var(--color-text-muted)]">{editUser.email}</p>
              <form onSubmit={submitEdit} className="mt-4 grid gap-3">
                <Field label="Email">
                  <input
                    className="field-input font-mono"
                    type="text"
                    inputMode="email"
                    autoComplete="email"
                    required
                    value={editEmail}
                    onChange={(e) => setEditEmail(e.target.value)}
                  />
                </Field>
                <Field label="Full name">
                  <input
                    className="field-input"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                  />
                </Field>
                <Field label="Phone" hint="Optional">
                  <input
                    className="field-input"
                    value={editPhone}
                    onChange={(e) => setEditPhone(e.target.value)}
                  />
                </Field>
                <div className="flex gap-2 pt-2">
                  <button type="submit" className="btn btn-primary flex-1" disabled={busy}>
                    Save
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary flex-1"
                    onClick={() => setEditUser(null)}
                    disabled={busy}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {pwUser && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
          >
            <div
              className="w-full max-w-md rounded-2xl bg-[var(--color-surface)] p-6"
              style={{ boxShadow: "var(--shadow-modal)" }}
            >
              <h2 className="text-lg font-semibold text-[var(--color-text)]">Reset password</h2>
              <p className="mt-1 font-mono text-xs text-[var(--color-text-muted)]">{pwUser.email}</p>
              <p className="mt-2 text-xs text-[var(--color-text-muted)]">
                Set a temporary password and tell the user out-of-band. Minimum 6 characters.
              </p>
              <form onSubmit={submitPw} className="mt-4 grid gap-3">
                <Field label="New password">
                  <input
                    className="field-input font-mono"
                    type="password"
                    autoComplete="new-password"
                    required
                    minLength={6}
                    value={pw1}
                    onChange={(e) => setPw1(e.target.value)}
                  />
                </Field>
                <Field label="Confirm">
                  <input
                    className="field-input font-mono"
                    type="password"
                    autoComplete="new-password"
                    required
                    minLength={6}
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                  />
                </Field>
                {pw1 && pw2 && pw1 !== pw2 && (
                  <p className="text-xs text-[var(--color-danger)]">Passwords do not match.</p>
                )}
                <div className="flex gap-2 pt-2">
                  <button
                    type="submit"
                    className="btn btn-primary flex-1"
                    disabled={busy || pw1.length < 6 || pw1 !== pw2}
                  >
                    Update password
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary flex-1"
                    onClick={() => setPwUser(null)}
                    disabled={busy}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {err && (
        <div className="rounded-lg border border-[rgba(255,59,48,0.25)] bg-[var(--color-danger-bg)] px-4 py-3 text-sm text-[var(--color-danger)]">
          {err}
        </div>
      )}

      <SfListShell
        objectLabel="Users"
        viewLabel={q.trim() ? `Matching “${q.trim()}”` : "All accounts"}
        metaRight={`${users.length} on this page · ${total.toLocaleString()} total`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {onCreate ? (
              <button
                type="button"
                className="btn btn-primary"
                onClick={onCreate}
                disabled={busy}
              >
                <UserPlus className="h-4 w-4" /> Create user
              </button>
            ) : null}
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => void load()}
              disabled={busy}
            >
              <RefreshCcw className={cn("h-4 w-4", busy && "spin")} /> Refresh
            </button>
          </div>
        }
        toolbar={
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setPage(0);
              setQ(qDraft.trim());
            }}
            className="flex flex-col gap-2 sm:flex-row sm:items-end"
          >
            <div className="relative min-w-0 flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-subtle)]" />
              <input
                className="field-input w-full py-2 pl-9 pr-3 text-sm"
                placeholder="Search this list…"
                value={qDraft}
                onChange={(e) => setQDraft(e.target.value)}
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              Apply
            </button>
          </form>
        }
        footer={
          total > pageSize ? (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>
                Page {page + 1} of {totalPages}
              </span>
              <div className="flex gap-1">
                <button
                  type="button"
                  className="btn btn-secondary px-2 py-1"
                  disabled={page === 0 || busy}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Prev
                </button>
                <button
                  type="button"
                  className="btn btn-secondary px-2 py-1"
                  disabled={page + 1 >= totalPages || busy}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          ) : null
        }
      >
        {users.length === 0 ? (
          <div className="p-6">
            <EmptyState>{busy ? "Loading…" : "No users match."}</EmptyState>
          </div>
        ) : (
          <SfTableWrap>
            <table className="dl-table">
              <thead>
                <tr>
                  <th className="w-10 text-center text-[var(--color-text-subtle)]">#</th>
                  <th className="w-10">
                    <input
                      type="checkbox"
                      checked={allDirRowsSelected}
                      onChange={() => toggleDirSelectAll()}
                      aria-label="Select all rows on page"
                      className="rounded border-[var(--color-border)]"
                    />
                  </th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th className="text-right tabular-nums">Workspaces</th>
                  <th>Status</th>
                  <th>Joined</th>
                  <th className="w-12" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {users.map((u, idx) => {
                  const isSelf = u.id === currentUserId;
                  return (
                    <tr key={u.id}>
                      <td className="text-center text-xs tabular-nums text-[var(--color-text-subtle)]">
                        {offset + idx + 1}
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={dirListSel.has(u.id)}
                          onChange={() => toggleDirRow(u.id)}
                          aria-label={`Select ${u.email}`}
                          className="rounded border-[var(--color-border)]"
                        />
                      </td>
                      <td className="text-sm font-medium text-[var(--color-text)]">
                        <Link
                          to={`/app/directory/${u.id}`}
                          className="text-[var(--color-accent)] hover:underline"
                        >
                          {u.display_name ?? "—"}
                        </Link>
                      </td>
                      <td className="font-mono text-[11px]">
                        <Link
                          to={`/app/directory/${u.id}`}
                          className="text-[var(--color-accent)] hover:underline"
                        >
                          {u.email}
                        </Link>
                      </td>
                      <td className="text-xs text-[var(--color-text-muted)]">{u.phone ?? "—"}</td>
                      <td className="text-right text-xs tabular-nums">{u.org_count}</td>
                      <td>
                        {u.active ? (
                          <span className="chip chip-blue">Active</span>
                        ) : (
                          <span className="chip chip-danger">Disabled</span>
                        )}
                      </td>
                      <td className="text-xs text-[var(--color-text-subtle)]">
                        {new Date(u.created_at).toLocaleDateString()}
                      </td>
                      <td className="text-right">
                        <SfRowMenu>
                          <Link
                            to={`/app/directory/${u.id}`}
                            className="block rounded-md px-3 py-2 text-left text-xs text-[var(--color-text)] hover:bg-[var(--color-surface-2)]"
                          >
                            Open record
                          </Link>
                          <SfRowMenuBtn onClick={() => openEdit(u)} disabled={busy}>
                            Edit profile…
                          </SfRowMenuBtn>
                          <SfRowMenuBtn onClick={() => openPw(u)} disabled={busy}>
                            Reset password…
                          </SfRowMenuBtn>
                          {!isSelf && (
                            <SfRowMenuBtn onClick={() => void toggleActive(u)} disabled={busy}>
                              {u.active ? "Deactivate" : "Activate"}
                            </SfRowMenuBtn>
                          )}
                          {!isSelf && (
                            <SfRowMenuBtn onClick={() => void removeUser(u)} disabled={busy}>
                              Delete…
                            </SfRowMenuBtn>
                          )}
                        </SfRowMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </SfTableWrap>
        )}
      </SfListShell>

      {editUser && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="w-full max-w-md rounded-2xl bg-[var(--color-surface)] p-6"
            style={{ boxShadow: "var(--shadow-modal)" }}
          >
            <h2 className="text-lg font-semibold text-[var(--color-text)]">Edit user</h2>
            <p className="mt-1 font-mono text-xs text-[var(--color-text-muted)]">{editUser.email}</p>
            <form onSubmit={submitEdit} className="mt-4 grid gap-3">
              <Field label="Email">
                <input
                  className="field-input font-mono"
                  type="text"
                  inputMode="email"
                  autoComplete="email"
                  required
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                />
              </Field>
              <Field label="Full name">
                <input
                  className="field-input"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                />
              </Field>
              <Field label="Phone" hint="Optional">
                <input
                  className="field-input"
                  value={editPhone}
                  onChange={(e) => setEditPhone(e.target.value)}
                />
              </Field>
              <div className="flex gap-2 pt-2">
                <button type="submit" className="btn btn-primary flex-1" disabled={busy}>
                  Save
                </button>
                <button
                  type="button"
                  className="btn btn-secondary flex-1"
                  onClick={() => setEditUser(null)}
                  disabled={busy}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {pwUser && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="w-full max-w-md rounded-2xl bg-[var(--color-surface)] p-6"
            style={{ boxShadow: "var(--shadow-modal)" }}
          >
            <h2 className="text-lg font-semibold text-[var(--color-text)]">Reset password</h2>
            <p className="mt-1 font-mono text-xs text-[var(--color-text-muted)]">{pwUser.email}</p>
            <p className="mt-2 text-xs text-[var(--color-text-muted)]">
              Set a temporary password and tell the user out-of-band. Minimum 6 characters.
            </p>
            <form onSubmit={submitPw} className="mt-4 grid gap-3">
              <Field label="New password">
                <input
                  className="field-input font-mono"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={6}
                  value={pw1}
                  onChange={(e) => setPw1(e.target.value)}
                />
              </Field>
              <Field label="Confirm">
                <input
                  className="field-input font-mono"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={6}
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                />
              </Field>
              {pw1 && pw2 && pw1 !== pw2 && (
                <p className="text-xs text-[var(--color-danger)]">Passwords do not match.</p>
              )}
              <div className="flex gap-2 pt-2">
                <button
                  type="submit"
                  className="btn btn-primary flex-1"
                  disabled={busy || pw1.length < 6 || pw1 !== pw2}
                >
                  Update password
                </button>
                <button
                  type="button"
                  className="btn btn-secondary flex-1"
                  onClick={() => setPwUser(null)}
                  disabled={busy}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────── Database explorer (dataly_admin only) ─────────── */

type DbMode = "browse" | "sql";

const WHERE_OPS: AdminWhereOp[] = [
  "=",
  "!=",
  "<",
  "<=",
  ">",
  ">=",
  "ilike",
  "is null",
  "is not null",
];

/**
 * Render a Postgres value into a stable string for the cell. We avoid React
 * trying to render plain objects (which throws) and clamp very long strings.
 */
function formatCell(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean" || typeof v === "bigint") {
    return String(v);
  }
  if (v instanceof Date) return v.toISOString();
  try {
    const s = JSON.stringify(v);
    return s ?? String(v);
  } catch {
    return String(v);
  }
}

function CellValue({ value }: { value: unknown }) {
  const text = formatCell(value);
  const isNull = value === null || value === undefined;
  return (
    <span
      className={cn(
        "block max-w-[260px] truncate font-mono text-[11px]",
        isNull && "italic text-[var(--color-text-subtle)]",
      )}
      title={text}
    >
      {text}
    </span>
  );
}

function DatabaseSection() {
  const [tables, setTables] = useState<AdminTable[]>([]);
  const [selectedKey, setSelectedKey] = useState<string>("");
  const [columns, setColumns] = useState<AdminColumn[]>([]);
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [orderBy, setOrderBy] = useState<string>("");
  const [orderDir, setOrderDir] = useState<"asc" | "desc">("desc");
  const [whereCol, setWhereCol] = useState<string>("");
  const [whereOp, setWhereOp] = useState<AdminWhereOp>("=");
  const [whereVal, setWhereVal] = useState<string>("");

  const [mode, setMode] = useState<DbMode>("browse");
  const [sql, setSql] = useState<string>("select id, email, created_at from public.users order by created_at desc limit 25;");
  const [sqlResult, setSqlResult] = useState<AdminQueryResult | null>(null);
  const [sqlError, setSqlError] = useState<string | null>(null);
  const [sqlBusy, setSqlBusy] = useState(false);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const selected = useMemo<AdminTable | null>(() => {
    if (!selectedKey) return null;
    const [schema, name] = selectedKey.split(".");
    return tables.find((t) => t.schema === schema && t.name === name) ?? null;
  }, [selectedKey, tables]);

  // Group tables by schema for the dropdown.
  const grouped = useMemo(() => {
    const m = new Map<string, AdminTable[]>();
    for (const t of tables) {
      const arr = m.get(t.schema) ?? [];
      arr.push(t);
      m.set(t.schema, arr);
    }
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [tables]);

  const loadTables = useCallback(async () => {
    setErr(null);
    try {
      const list = await adminListTables();
      setTables(list);
      setSelectedKey((prev) => {
        if (prev && list.some((t) => `${t.schema}.${t.name}` === prev)) return prev;
        const first = list.find((t) => t.schema === "public") ?? list[0];
        return first ? `${first.schema}.${first.name}` : "";
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load tables");
    }
  }, []);

  useEffect(() => {
    void loadTables();
  }, [loadTables]);

  const loadColumns = useCallback(async (t: AdminTable) => {
    const cols = await adminTableColumns(t.schema, t.name);
    setColumns(cols);
    // Pick a sane default order column.
    const idCol = cols.find((c) => c.name === "id");
    const createdCol = cols.find((c) => c.name === "created_at");
    setOrderBy(createdCol?.name ?? idCol?.name ?? cols[0]?.name ?? "");
    setOrderDir(createdCol ? "desc" : "asc");
    setWhereCol("");
    setWhereOp("=");
    setWhereVal("");
    setPage(0);
  }, []);

  useEffect(() => {
    if (selected) {
      void loadColumns(selected).catch((e) =>
        setErr(e instanceof Error ? e.message : "Failed to load columns"),
      );
    }
  }, [selected, loadColumns]);

  const fetchRows = useCallback(async () => {
    if (!selected) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await adminTableRows(selected.schema, selected.name, {
        limit: pageSize,
        offset: page * pageSize,
        orderBy: orderBy || undefined,
        orderDir,
        whereCol: whereCol || undefined,
        whereOp: whereCol ? whereOp : undefined,
        whereVal: whereCol && whereOp !== "is null" && whereOp !== "is not null" ? whereVal : undefined,
      });
      setRows(r.rows);
      setTotal(r.total);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Query failed");
      setRows([]);
      setTotal(0);
    } finally {
      setBusy(false);
    }
  }, [selected, pageSize, page, orderBy, orderDir, whereCol, whereOp, whereVal]);

  useEffect(() => {
    if (mode === "browse") void fetchRows();
  }, [fetchRows, mode]);

  async function runSql() {
    setSqlBusy(true);
    setSqlError(null);
    try {
      const r = await adminRunQuery(sql);
      setSqlResult(r);
    } catch (e) {
      setSqlResult(null);
      setSqlError(e instanceof Error ? e.message : "Query failed");
    } finally {
      setSqlBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-5">
      {err && (
        <div className="rounded-lg border border-[rgba(255,59,48,0.25)] bg-[var(--color-danger-bg)] px-4 py-3 text-sm text-[var(--color-danger)]">
          {err}
        </div>
      )}

      <Card
        title="Workbench"
        description="Read-only. Every query runs inside BEGIN READ ONLY with a hard 5–8s timeout."
        action={
          <div className="flex items-center gap-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] p-0.5">
            {(["browse", "sql"] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                className={cn(
                  "rounded-md px-3 py-1 text-xs font-medium capitalize transition",
                  mode === m
                    ? "bg-[var(--color-surface)] text-[var(--color-text)] shadow-sm"
                    : "text-[var(--color-text-muted)] hover:text-[var(--color-text)]",
                )}
              >
                {m === "browse" ? (
                  <span className="inline-flex items-center gap-1.5">
                    <TableIcon className="h-3.5 w-3.5" /> Browse
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5">
                    <Code2 className="h-3.5 w-3.5" /> SQL
                  </span>
                )}
              </button>
            ))}
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[280px,1fr,auto] sm:items-end">
          <Field label="Table">
            <select
              className="field-input"
              value={selectedKey}
              onChange={(e) => setSelectedKey(e.target.value)}
            >
              <option value="">Select a table…</option>
              {grouped.map(([schema, list]) => (
                <optgroup key={schema} label={schema}>
                  {list.map((t) => (
                    <option key={`${t.schema}.${t.name}`} value={`${t.schema}.${t.name}`}>
                      {t.name} {t.type === "VIEW" ? "(view)" : ""}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </Field>
          <div className="text-xs text-[var(--color-text-muted)]">
            <strong className="text-[var(--color-text)]">{tables.length}</strong> tables ·{" "}
            <strong className="text-[var(--color-text)]">{columns.length}</strong> columns in
            current table
          </div>
          <button
            type="button"
            onClick={() => void loadTables()}
            className="btn btn-ghost"
            title="Reload table list"
          >
            <RefreshCcw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>
      </Card>

      {mode === "browse" && selected && (
        <>
          <Card
            title="Filters"
            description="Filter, order, and paginate rows. Stays read-only end-to-end."
          >
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setPage(0);
                void fetchRows();
              }}
              className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[1fr,140px,1fr,1fr,140px,auto] lg:items-end"
            >
              <Field label="Where column">
                <select
                  className="field-input"
                  value={whereCol}
                  onChange={(e) => setWhereCol(e.target.value)}
                >
                  <option value="">— none —</option>
                  {columns.map((c) => (
                    <option key={c.name} value={c.name}>
                      {c.name} ({c.type})
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Op">
                <select
                  className="field-input"
                  value={whereOp}
                  onChange={(e) => setWhereOp(e.target.value as AdminWhereOp)}
                  disabled={!whereCol}
                >
                  {WHERE_OPS.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Value">
                <input
                  className="field-input font-mono"
                  value={whereVal}
                  onChange={(e) => setWhereVal(e.target.value)}
                  disabled={!whereCol || whereOp === "is null" || whereOp === "is not null"}
                  placeholder={whereOp === "ilike" ? "%search%" : ""}
                />
              </Field>
              <Field label="Order by">
                <select
                  className="field-input"
                  value={orderBy}
                  onChange={(e) => setOrderBy(e.target.value)}
                >
                  <option value="">— none —</option>
                  {columns.map((c) => (
                    <option key={c.name} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Direction">
                <select
                  className="field-input"
                  value={orderDir}
                  onChange={(e) => setOrderDir(e.target.value as "asc" | "desc")}
                  disabled={!orderBy}
                >
                  <option value="asc">asc</option>
                  <option value="desc">desc</option>
                </select>
              </Field>
              <button type="submit" className="btn btn-primary" disabled={busy}>
                <Filter className="h-3.5 w-3.5" /> Apply
              </button>
            </form>
          </Card>

          <Card
            title={
              <span className="inline-flex items-center gap-2">
                <Database className="h-4 w-4 text-[var(--color-accent)]" />
                <code className="font-mono text-[12px] text-[var(--color-text)]">
                  {selected.schema}.{selected.name}
                </code>
              </span>
            }
            description={`Page ${page + 1} of ${Math.max(1, Math.ceil(total / pageSize))} · ${total.toLocaleString()} row${total === 1 ? "" : "s"}`}
            action={
              <div className="flex items-center gap-2">
                <select
                  className="control-input"
                  value={pageSize}
                  onChange={(e) => {
                    setPage(0);
                    setPageSize(Number(e.target.value));
                  }}
                >
                  {[25, 50, 100, 200, 500, 1000].map((n) => (
                    <option key={n} value={n}>
                      {n} / page
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn btn-ghost px-2 py-1"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0 || busy}
                  title="Previous page"
                >
                  <ChevronLeftIcon className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  className="btn btn-ghost px-2 py-1"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={(page + 1) * pageSize >= total || busy}
                  title="Next page"
                >
                  <ChevronRightIcon className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => void fetchRows()}
                  className="btn btn-ghost px-2 py-1"
                  disabled={busy}
                  title="Re-run query"
                >
                  <RefreshCcw className="h-3.5 w-3.5" />
                </button>
              </div>
            }
          >
            {rows.length === 0 ? (
              <EmptyState>{busy ? "Loading…" : "No rows."}</EmptyState>
            ) : (
              <div className="overflow-x-auto">
                <table className="dl-table">
                  <thead>
                    <tr>
                      {columns.map((c) => (
                        <th key={c.name} title={c.type}>
                          {c.name}
                          <span className="ml-1 text-[10px] font-normal text-[var(--color-text-subtle)]">
                            {c.type}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => (
                      <tr key={i}>
                        {columns.map((c) => (
                          <td key={c.name}>
                            <CellValue value={row[c.name]} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      {mode === "sql" && (
        <Card
          title="Ad-hoc SELECT"
          description="One statement, SELECT/WITH/TABLE/VALUES only. Auto-rolled back; 8s statement_timeout. Audit-logged."
          action={
            <button
              type="button"
              onClick={() => void runSql()}
              className="btn btn-primary"
              disabled={sqlBusy || !sql.trim()}
            >
              <Play className="h-3.5 w-3.5" /> {sqlBusy ? "Running…" : "Run"}
            </button>
          }
        >
          <textarea
            spellCheck={false}
            className="field-input h-40 w-full font-mono text-[12px] leading-5"
            value={sql}
            onChange={(e) => setSql(e.target.value)}
            placeholder="select * from public.organizations order by created_at desc limit 50"
          />
          {sqlError && (
            <p className="mt-3 rounded-lg border border-[rgba(255,59,48,0.25)] bg-[var(--color-danger-bg)] px-3 py-2 font-mono text-[11px] text-[var(--color-danger)]">
              {sqlError}
            </p>
          )}
          {sqlResult && (
            <div className="mt-4">
              <p className="mb-2 text-xs text-[var(--color-text-muted)]">
                {sqlResult.row_count.toLocaleString()} row
                {sqlResult.row_count === 1 ? "" : "s"} · {sqlResult.fields.length} column
                {sqlResult.fields.length === 1 ? "" : "s"}
              </p>
              {sqlResult.rows.length === 0 ? (
                <EmptyState>Query returned no rows.</EmptyState>
              ) : (
                <div className="overflow-x-auto">
                  <table className="dl-table">
                    <thead>
                      <tr>
                        {sqlResult.fields.map((f) => (
                          <th key={f.name}>{f.name}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sqlResult.rows.slice(0, 1000).map((r, i) => (
                        <tr key={i}>
                          {r.map((v, j) => (
                            <td key={j}>
                              <CellValue value={v} />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {sqlResult.rows.length > 1000 && (
                    <p className="mt-2 text-xs text-[var(--color-text-subtle)]">
                      Truncated to 1000 of {sqlResult.rows.length.toLocaleString()} rows.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
